// data/sample.js — bundled stand-ins for the sources a browser cannot call.
//
// CONTRACT (tests/test_contract.mjs):
//   export function sampleAsteroids(): Record[]
//   export function sampleDeepSpace(): Record[]
//   export function sampleReentries(): Record[]
//
// Every record here carries cls:'sample' and a meta.why saying what it stands in for and why it
// is not live. It is drawn with a dashed halo so it is never mistaken for a live position.
//
// Three builders here are NOT stand-ins and say so in their own cls: handKeptSites() (measured),
// sampleOddities() (each row's own class) and farBodies() (inferred: JPL's orbits with their real
// phase, the far-bodies layer). They live in this file because it is where hand-kept rows that
// ship with the app already are.
//
// ---------------------------------------------------------------------------------------------
// THE HONESTY RULES THIS FILE IS WRITTEN UNDER
//
//  1. A rounded number with meta.approx = true is correct. A nine-figure number I cannot defend
//     is not, however much more finished it looks.
//  2. meta.approxFields names EXACTLY which numbers are soft, so a card can say so in words
//     instead of hedging about the whole record.
//  3. Where a phase along the orbit is unknown it is set to perihelion at the stated epoch and
//     said so out loud. Every asteroid below therefore sits at ITS OWN perihelion: the orbit's
//     size, shape and plane are right, the place along it is a placeholder. That reads as a
//     placeholder at a glance, which is the point — a plausible-looking wrong phase would not.
//  4. Where a phase IS knowable it is used. The craft anchored to Earth below use Earth's
//     published J2000 mean anomaly, so their positions are real; the craft orbiting other worlds
//     start from JPL's own position for them (deep space, construction D).
//
// Element shape matches data/parsers.js parseComets() exactly, so one `kepler` propagator eats
// both: {qKm, e, aKm, iRad, omRad, wRad, tpMs, epochMs, muKm3S2}. Sampled records carry
// {tMs, rKm:[x,y,z], vKmS:[vx,vy,vz]} in sun-inertial (heliocentric ecliptic J2000).

const DEG = Math.PI / 180;
const AU_KM = 149597870.7;
const MU_SUN = 1.32712440018e11; // km^3/s^2
const DAY_MS = 86400000;

/** J2000.0 = 2000-01-01 12:00 TT. The epoch the planetary mean anomalies below are stated at. */
const J2000_MS = Date.UTC(2000, 0, 1, 12, 0, 0);

// Shown on the card of every deep-space craft and sample asteroid, JWST and Voyager among them. It used
// to say "answer 200 with no Access-Control-Allow-Origin header" and "until the harvester runs": true,
// and written for whoever built the app. The term stays, in brackets, for anyone who wants to look
// it up.
const NO_CORS =
  "NASA's JPL servers do not let a page on another website read them (they send no CORS header), " +
  'so this browser cannot fetch them. Until our own server copies them, this is data shipped with the app.';

// =================================================================================================
// Asteroids
// =================================================================================================
//
// a, e, i, node and argument of perihelion are the published osculating elements, rounded to the
// digits given — four to six significant figures, which is where my confidence ends. The mean
// anomaly is NOT known here (see honesty rule 3): each object is placed at its own perihelion at
// the epoch below and the card must say so.

const ASTEROID_EPOCH_MS = Date.UTC(2026, 0, 1);

/** @type {Array<{name,designation,aAu,e,iDeg,nodeDeg,argpDeg,hMag,diameterKm,neo,note,why?}>} */
const ASTEROIDS = [
  {
    id: 'asteroid-99942',
    name: 'Apophis',
    designation: '99942 Apophis',
    aAu: 0.9224,
    e: 0.1915,
    iDeg: 3.34,
    nodeDeg: 203.96,
    argpDeg: 126.72,
    hMag: 19.7,
    diameterKm: 0.34,
    neo: true,
    note: 'It passes inside the ring of geostationary satellites on 13 April 2029, and it will ' +
      'be visible to the naked eye from Europe and Africa.',
  },
  {
    id: 'asteroid-101955',
    name: 'Bennu',
    designation: '101955 Bennu',
    aAu: 1.1264,
    e: 0.2037,
    iDeg: 6.03,
    nodeDeg: 2.06,
    argpDeg: 66.22,
    hMag: 20.2,
    diameterKm: 0.49,
    neo: true,
    note: 'OSIRIS-REx brought 122 grams of it back to the Utah desert in September 2023.',
  },
  {
    id: 'asteroid-433',
    name: 'Eros',
    designation: '433 Eros',
    aAu: 1.4583,
    e: 0.2226,
    iDeg: 10.83,
    nodeDeg: 304.3,
    argpDeg: 178.8,
    // VERIFIED from JPL SBDB on 2026-09-06. The evidence is this comment: this repo is code
    // only, so a row that points at a docs/ file points at nothing a reader can open.
    hMag: 10.4,
    diameterKm: 16.84,
    neo: true,
    note: 'The first asteroid anything ever orbited, and then landed on: NEAR Shoemaker, 2001.',
  },
  {
    id: 'asteroid-162173',
    name: 'Ryugu',
    designation: '162173 Ryugu',
    aAu: 1.1896,
    e: 0.1902,
    iDeg: 5.88,
    nodeDeg: 251.6,
    argpDeg: 211.4,
    hMag: 19.2,
    diameterKm: 0.9,
    neo: true,
    note: 'Hayabusa2 shot it, landed on it twice and brought 5 grams home in 2020.',
  },
  // CERES MOVED OUT on 2026-09-22, to FAR_BODIES below. Here it sat at its own perihelion (rule 3)
  // and only on the day the NEO snapshot was missing -- the live close-approach table never lists
  // it, so on a normal day the largest thing in the asteroid belt was on nobody's map. There it has
  // JPL's real phase and a layer that is always loaded.
  {
    id: 'asteroid-4',
    name: 'Vesta',
    designation: '4 Vesta',
    aAu: 2.3617,
    e: 0.0894,
    iDeg: 7.14,
    nodeDeg: 103.81,
    argpDeg: 151.2,
    hMag: 3.2,
    diameterKm: 525,
    neo: false,
    note: 'The only asteroid bright enough to see without a telescope, from a dark place.',
  },
  {
    id: 'asteroid-65803',
    name: 'Didymos',
    designation: '65803 Didymos',
    aAu: 1.6446,
    e: 0.3838,
    iDeg: 3.41,
    nodeDeg: 73.2,
    argpDeg: 319.3,
    hMag: 18.16,
    diameterKm: 0.78,
    neo: true,
    note: 'DART hit its small moon Dimorphos in 2022 and shortened its orbit by 32 minutes — ' +
      'the first time people moved another world.',
  },
  {
    id: 'asteroid-25143',
    name: 'Itokawa',
    designation: '25143 Itokawa',
    aAu: 1.324,
    e: 0.28,
    iDeg: 1.62,
    nodeDeg: 69.1,
    argpDeg: 162.8,
    hMag: 19.2,
    diameterKm: 0.33,
    neo: true,
    note: 'A rubble pile the shape of a peanut. Hayabusa returned about 1 500 grains of it in 2010.',
  },
  {
    id: 'asteroid-2',
    name: 'Pallas',
    designation: '2 Pallas',
    aAu: 2.772,
    e: 0.2299,
    iDeg: 34.84,
    nodeDeg: 172.9,
    argpDeg: 310.9,
    hMag: 4.13,
    diameterKm: 512,
    neo: false,
    note: 'Its orbit is tilted 35 degrees out of the plane everything else moves in, which is ' +
      'why no spacecraft has ever been sent.',
  },
  {
    id: 'asteroid-3200',
    name: 'Phaethon',
    designation: '3200 Phaethon',
    aAu: 1.2712,
    e: 0.8898,
    iDeg: 22.26,
    nodeDeg: 265.2,
    argpDeg: 322.2,
    hMag: 14.6,
    diameterKm: 5.8,
    neo: true,
    note: 'The Geminids come from this one. It swings closer to the Sun than Mercury does and ' +
      'sheds dust when it gets there.',
  },
];

/** @returns {Array<Object>} */
export function sampleAsteroids() {
  return ASTEROIDS.map((a) => {
    const aKm = a.aAu * AU_KM;
    const qKm = aKm * (1 - a.e);
    return {
      id: a.id,
      name: a.name,
      layer: 'asteroids',
      klass: 'asteroid',
      propagator: 'kepler',
      frame: 'sun-inertial',
      cls: 'sample',
      epoch: ASTEROID_EPOCH_MS,
      source: 'jpl-sbdb-neo',
      elements: {
        qKm,
        e: a.e,
        aKm,
        iRad: a.iDeg * DEG,
        omRad: a.nodeDeg * DEG,
        wRad: a.argpDeg * DEG,
        // Honesty rule 3: perihelion at the epoch, because the phase is not known here.
        tpMs: ASTEROID_EPOCH_MS,
        epochMs: ASTEROID_EPOCH_MS,
        muKm3S2: MU_SUN,
      },
      meta: {
        designation: a.designation,
        aAu: a.aAu,
        qAu: qKm / AU_KM,
        eccentricity: a.e,
        inclinationDeg: a.iDeg,
        nodeDeg: a.nodeDeg,
        argpDeg: a.argpDeg,
        periodDays: periodDays(aKm),
        absoluteMagnitude: a.hMag,
        diameterKm: a.diameterKm,
        neo: a.neo,
        note: a.note,
        approx: true,
        approxFields: ['tpMs', 'aAu', 'eccentricity', 'inclinationDeg', 'nodeDeg', 'argpDeg'],
        why:
          NO_CORS +
          ' The orbit here is the published one rounded to the figures shown, so its size, ' +
          'shape and tilt are right. Where it is ALONG that orbit is not known to this page: ' +
          'it is drawn at its closest point to the Sun on 1 January 2026, which is a ' +
          'placeholder, not a position.',
      },
    };
  });
}

// =================================================================================================
// Dwarf planets and far travellers
// =================================================================================================
//
// Eight dwarf planets and the two objects known to have come from another star, each on JPL's own
// orbit WITH ITS PHASE, so each is drawn where it is today. That is the difference from the
// asteroids above, and why these are not rows there: those sit at a placeholder perihelion (rule
// 3), carry cls 'sample', and appear only when the NEO snapshot is missing. These stand in for
// nothing -- like the oddities, they are the thing -- so they get their own always-loaded layer
// (`far-bodies` in data/layers.js and registry/layers.yaml) and cls 'inferred', exactly as a live
// SBDB record is: JPL measured the orbit, this page computes the position from it.
//
// THE EVIDENCE IS THIS BLOCK, as it is for Eros above: this repository is code only.
//   Elements   JPL Small-Body Database API, one query per row:
//                https://ssd-api.jpl.nasa.gov/sbdb.api?sstr=<number or designation>&phys-par=1&full-prec=1
//              (read 2026-09-22). Heliocentric ecliptic J2000 osculating elements, kept to nine
//              significant figures -- JPL's fit, not a rounding of mine, so rule 1's worry about
//              undefendable digits does not apply; nine is where the position stops changing at
//              the scale anything here is drawn. Each row names its epoch, the date JPL solved the
//              orbit, and the last observation in the fit.
//   Checked    against JPL Horizons, heliocentric vectors for 2026-09-22 00:00 TDB
//              (https://ssd.jpl.nasa.gov/api/horizons.api?COMMAND='136199;'&EPHEM_TYPE=VECTORS&CENTER='500@10'&REF_PLANE=ECLIPTIC
//              and the same for each row). Two-body motion from these elements lands within
//              0.0001 au of Horizons for all eight dwarf planets. The two interstellar objects miss
//              by 0.028 au ('Oumuamua) and 0.018 au (Borisov): nine and seven years of pull from the
//              planets and of the small non-gravitational push both showed, which two-body motion
//              leaves out -- a few million km, at 50 au. tests/test_far_bodies.mjs holds all ten.
//   Facts      diameters, moons and each `why` line: the Wikipedia article named in `whySource`,
//              its infobox and lead (read 2026-09-22), except Ceres's diameter, which is the SBDB's
//              own (Park et al. 2016, Nature 537). "Dwarf planet" for all eight is Wikipedia's
//              "Dwarf planet" lead: astronomers are in general agreement that the nine largest
//              candidates are dwarf planets, and these are eight of the nine -- Pluto, the ninth, is
//              a world in registry/worlds.yaml, not a row here.
//   Hyperbolic 'Oumuamua (e 1.20) and Borisov (e 3.36) are not bound to the Sun. propagate/kepler.js
//              has solved the hyperbolic branch since the MPC comets needed it (safeguarded Newton,
//              solveKeplerHyperbolic); both rows use the mean-anomaly form JPL publishes, which the
//              normaliser there turns into time since perihelion on either branch.

/** JPL's epoch for every dwarf-planet row: JD 2461200.5 = 2026-06-09 00:00 TDB. */
const SBDB_EPOCH_JD = 2461200.5;

/**
 * @type {Array<{id, name, designation, aliases, klass, farKind, aAu, e, iDeg, nodeDeg, argpDeg,
 *   maDeg, epochJd, tpJd, solution, lastObs, hMag, diameterKm, diameterLowKm?, diameterHighKm?,
 *   moons, why, whySource, departure?, orbitCaveat?}>}
 */
const FAR_BODIES = [
  {
    id: 'dwarf-ceres',
    name: 'Ceres',
    designation: '1 Ceres',
    aliases: ['1 Ceres', '(1) Ceres', 'A801 AA', 'dwarf planet'],
    klass: 'asteroid',
    farKind: 'dwarf',
    // sstr=1. Solved 2021-04-13, observations 1995-01-05 to 2021-01-28, re-osculated by JPL to
    // the current epoch.
    aAu: 2.7655526, e: 0.0796922951, iDeg: 10.5880278, nodeDeg: 80.2486268, argpDeg: 73.2942145,
    maDeg: 274.419346, epochJd: SBDB_EPOCH_JD, tpJd: 2461599.8415,
    solution: '2021-04-13', lastObs: '2021-01-28',
    hMag: 3.34,
    diameterKm: 939.4, // SBDB phys-par: Park et al. 2016, Nature 537, 515
    moons: [], // "the largest without a moon" (Wikipedia, Ceres)
    why: 'The largest thing in the asteroid belt, a quarter the width of the Moon. NASA’s Dawn went ' +
      'into orbit around it in 2015 and is still there, switched off.',
    whySource: 'Wikipedia, Ceres (dwarf planet), read 22 September 2026',
  },
  {
    id: 'dwarf-eris',
    name: 'Eris',
    designation: '136199 Eris',
    // "Xena" is the discoverers' nickname before the name, in the article's infobox.
    aliases: ['136199', '136199 Eris', '2003 UB313', 'Xena', 'dwarf planet'],
    klass: 'asteroid',
    farKind: 'dwarf',
    // sstr=136199. Solved 2026-06-06, observations 1954-09-03 to 2026-02-06.
    aAu: 67.9339469, e: 0.438238535, iDeg: 43.9258279, nodeDeg: 36.0047704, argpDeg: 150.794924,
    maDeg: 211.774434, epochJd: SBDB_EPOCH_JD, tpJd: 2545407.7168,
    solution: '2026-06-06', lastObs: '2026-02-06',
    hMag: -1.26,
    diameterKm: 2326,
    moons: ['Dysnomia'],
    why: 'It is 27% more massive than Pluto, though slightly smaller. Finding it in 2005 is what ' +
      'pushed astronomers to define the word planet, in 2006.',
    whySource: 'Wikipedia, Eris (dwarf planet), read 22 September 2026',
  },
  {
    id: 'dwarf-haumea',
    name: 'Haumea',
    designation: '136108 Haumea',
    aliases: ['136108', '136108 Haumea', '2003 EL61', 'dwarf planet'],
    klass: 'asteroid',
    farKind: 'dwarf',
    // sstr=136108. Solved 2026-06-06, observations 1955-03-22 to 2026-04-14.
    aAu: 43.0602902, e: 0.194443015, iDeg: 28.2084739, nodeDeg: 121.786056, argpDeg: 240.690547,
    maDeg: 223.210412, epochJd: SBDB_EPOCH_JD, tpJd: 2500416.5996,
    solution: '2026-06-06', lastObs: '2026-04-14',
    hMag: 0.14,
    // The mean of 2 122 x 1 688 x 1 036 km (infobox). Drawn round -- the departure says so.
    diameterKm: 1544,
    moons: ['Hiʻiaka', 'Namaka'],
    // 3.9154 h is the SBDB's own rotation period (LCDB).
    why: 'It spins once every 3.9 hours and is twice as long as it is thick. In 2017 it became the ' +
      'first dwarf planet found to have a ring.',
    whySource: 'Wikipedia, Haumea, read 22 September 2026; rotation from the JPL Small-Body Database',
    departure: 'the real Haumea is an elongated egg, about twice as long as it is thick',
  },
  {
    id: 'dwarf-makemake',
    name: 'Makemake',
    designation: '136472 Makemake',
    aliases: ['136472', '136472 Makemake', '2005 FY9', 'dwarf planet'],
    klass: 'asteroid',
    farKind: 'dwarf',
    // sstr=136472. Solved 2026-06-06, observations 1955-01-29 to 2026-04-05.
    aAu: 45.5709332, e: 0.158888995, iDeg: 29.027856, nodeDeg: 79.2948338, argpDeg: 297.092273,
    maDeg: 169.937996, epochJd: SBDB_EPOCH_JD, tpJd: 2408158.6941,
    solution: '2026-06-06', lastObs: '2026-04-05',
    hMag: -0.25,
    diameterKm: 1430, // twice the infobox's mean radius, 715 km
    moons: ['S/2015 (136472) 1'], // found in 2015, still unnamed
    why: 'Covered in frozen methane stained reddish-brown. Found in 2005, it is one of the ' +
      'discoveries that made Pluto a dwarf planet in 2006.',
    whySource: 'Wikipedia, Makemake, read 22 September 2026',
  },
  {
    id: 'dwarf-sedna',
    name: 'Sedna',
    designation: '90377 Sedna',
    aliases: ['90377', '90377 Sedna', '2003 VB12', 'dwarf planet'],
    klass: 'asteroid',
    farKind: 'dwarf',
    // sstr=90377. Solved 2026-06-06, observations 1990-09-25 to 2026-02-27. The osculating
    // heliocentric a (544 au) is not the orbit's size in the long run: the barycentric one is 506
    // au and the lap about 11 400 years, which is the figure the `why` line gives. Near where
    // Sedna is now, the two ellipses are the same curve to far better than a pixel.
    aAu: 543.719529, e: 0.859882459, iDeg: 11.9252758, nodeDeg: 144.506166, argpDeg: 311.098773,
    maDeg: 358.595694, epochJd: SBDB_EPOCH_JD, tpJd: 2479264.7507,
    solution: '2026-06-06', lastObs: '2026-02-27',
    hMag: 1.5,
    // 906 km, +314 / -258 (Lellouch et al. 2013, in the infobox): nobody has weighed or resolved
    // it, so the card prints the range, not the middle.
    diameterKm: 906,
    diameterLowKm: 648,
    diameterHighKm: 1220,
    moons: [],
    why: 'One lap of the Sun takes it about 11 400 years, and even at its closest it is more than ' +
      'twice as far out as Neptune.',
    whySource: 'Wikipedia, Sedna (dwarf planet), read 22 September 2026',
  },
  {
    id: 'dwarf-gonggong',
    name: 'Gonggong',
    designation: '225088 Gonggong',
    aliases: ['225088', '225088 Gonggong', '2007 OR10', 'dwarf planet'],
    klass: 'asteroid',
    farKind: 'dwarf',
    // sstr=225088. Solved 2026-08-27, observations 1985-08-19 to 2025-10-15.
    aAu: 66.8666657, e: 0.504251, iDeg: 30.8990672, nodeDeg: 336.838316, argpDeg: 206.623284,
    maDeg: 111.664542, epochJd: SBDB_EPOCH_JD, tpJd: 2399252.7247,
    solution: '2026-08-27', lastObs: '2025-10-15',
    hMag: 1.82,
    diameterKm: 1230,
    moons: ['Xiangliu'],
    // 31 degrees is the SBDB inclination above; 33 to 101 au are its perihelion and aphelion.
    why: 'Its orbit swings it between 33 and 101 au from the Sun, tilted 31 degrees out of the ' +
      'plane the planets move in. It has one moon, Xiangliu.',
    whySource: 'Wikipedia, Gonggong (dwarf planet), read 22 September 2026',
  },
  {
    id: 'dwarf-quaoar',
    name: 'Quaoar',
    designation: '50000 Quaoar',
    aliases: ['50000', '50000 Quaoar', '2002 LM60', 'dwarf planet'],
    klass: 'asteroid',
    farKind: 'dwarf',
    // sstr=50000. Solved 2026-06-06, observations 1954-05-25 to 2025-08-31.
    aAu: 43.1561765, e: 0.0352002368, iDeg: 7.9915758, nodeDeg: 188.919125, argpDeg: 163.209051,
    maDeg: 292.848757, epochJd: SBDB_EPOCH_JD, tpJd: 2480516.3765,
    solution: '2026-06-06', lastObs: '2025-08-31',
    hMag: 2.41,
    diameterKm: 1098, // volume-equivalent, 1 097.6 km (Proudfoot et al. 2025, in the infobox)
    moons: ['Weywot'], // one confirmed; a second is unconfirmed and not listed
    why: 'It has two thin rings, farther out than theory said rings could last, and one moon, Weywot.',
    whySource: 'Wikipedia, Quaoar, read 22 September 2026',
  },
  {
    id: 'dwarf-orcus',
    name: 'Orcus',
    designation: '90482 Orcus',
    aliases: ['90482', '90482 Orcus', '2004 DW', 'dwarf planet'],
    klass: 'asteroid',
    farKind: 'dwarf',
    // sstr=90482. Solved 2026-06-06, observations 1951-11-08 to 2026-04-13.
    aAu: 39.3768654, e: 0.220524063, iDeg: 20.5568102, nodeDeg: 268.405352, argpDeg: 73.5684861,
    maDeg: 189.097291, epochJd: SBDB_EPOCH_JD, tpJd: 2504046.135,
    solution: '2026-06-06', lastObs: '2026-04-13',
    hMag: 2.13,
    diameterKm: 910, // +50 / -40 km (Brown 2018, in the infobox)
    moons: ['Vanth'],
    why: 'It keeps Pluto’s rhythm, two laps of the Sun for every three of Neptune’s, but in the ' +
      'opposite phase: when Pluto is closest in, Orcus is farthest out.',
    whySource: 'Wikipedia, Orcus (dwarf planet), read 22 September 2026',
  },
  {
    id: 'interstellar-1i',
    name: 'ʻOumuamua',
    designation: '1I/2017 U1',
    aliases: ['Oumuamua', "'Oumuamua", '1I', '1I/2017 U1', 'A/2017 U1', 'interstellar object'],
    // JPL files it as an asteroid (kind `au`, prefix A): it showed no coma.
    klass: 'asteroid',
    farKind: 'interstellar',
    // sstr=1I. Solved 2018-06-26, observations 2017-10-14 to 2018-01-02. JPL's fit includes the
    // non-gravitational acceleration; two-body motion from its osculating elements does not.
    aAu: -1.27234501, e: 1.2011338, iDeg: 122.741706, nodeDeg: 24.5969096, argpDeg: 241.810536,
    maDeg: 51.1576198, epochJd: 2458080.5, tpJd: 2458006.0073,
    solution: '2018-06-26', lastObs: '2018-01-02',
    hMag: 22.08,
    // No diameter: "between 100 and 1000 m long" is a range of shapes, not a size. The asteroid
    // builder draws its default rock, and the departure says what is not known.
    diameterKm: null,
    moons: null,
    why: 'The first object ever seen passing through the Solar System from another star, found on ' +
      '19 October 2017. It sped up slightly on the way out, and nobody is sure why.',
    whySource: 'Wikipedia, 1I/ʻOumuamua, read 22 September 2026',
    departure: 'nobody has seen its shape; the way its brightness changed says it is much longer or flatter than this',
    orbitCaveat: 'where it is now is worked out from JPL’s orbit, not seen',
  },
  {
    id: 'interstellar-2i',
    name: '2I/Borisov',
    designation: 'C/2019 Q4 (Borisov)',
    aliases: ['Borisov', 'C/2019 Q4', 'C/2019 Q4 (Borisov)', '2I', 'interstellar object', 'interstellar comet'],
    klass: 'comet',
    farKind: 'interstellar',
    // sstr=2I. Solved 2024-06-24, observations 2019-02-24 to 2020-09-30.
    aAu: -0.851492255, e: 3.35647578, iDeg: 44.0526425, nodeDeg: 308.147729, argpDeg: 209.123686,
    maDeg: 34.4294703, epochJd: 2458853.5, tpJd: 2458826.0528,
    solution: '2024-06-24', lastObs: '2020-09-30',
    hMag: null, // a comet's M1 (13.8) is not an asteroid's H, and nothing here reads it
    diameterKm: null, // "at most 0.4 to 0.5 km" is an upper limit, not a size
    moons: null,
    why: 'The first comet seen coming from another star, found on 29 August 2019 by Gennadiy Borisov, ' +
      'an amateur astronomer and telescope maker in Crimea.',
    whySource: 'Wikipedia, 2I/Borisov, read 22 September 2026',
    // scene/models.js shows a selected comet's tails at any distance, so it reads as a comet. Seen
    // in the browser on 2026-09-22: two tails on a comet 48 au out that nobody has seen since 2020.
    departure: 'the tails are part of the drawing, not an observation: nobody has seen Borisov since September 2020',
    orbitCaveat: 'where it is now is worked out from JPL’s orbit, not seen',
  },
  // 0079 a6: begin -- the third visitor. JPL's solution of 2026-10-21 (SBDB sstr=3I, read 2026-10-29), nine significant
  // figures as for the other two. Hyperbolic like Borisov (e 6.12, the most open orbit of the three), retrograde (i 175 degrees).
  // Its perihelion, JD 2460977.94, is 29 October 2025; NASA Science says "around Oct. 30, 2025, at a distance of about 1.4 au".
  {
    id: 'interstellar-3i',
    name: '3I/ATLAS',
    designation: 'C/2025 N1 (ATLAS)',
    aliases: ['ATLAS', 'C/2025 N1', 'C/2025 N1 (ATLAS)', '3I', 'interstellar object', 'interstellar comet'],
    // JPL files it as a comet (kind `cu`); the ATLAS survey's telescope in Rio Hurtado, Chile reported it on 1 July 2025.
    klass: 'comet',
    farKind: 'interstellar',
    // sstr=3I. Solved 2026-10-21, observations 2025-10-03 to 2026-10-16 (117 used; JPL's fit includes the non-gravitational terms A1 to A3).
    aAu: -0.263883522, e: 6.12346739, iDeg: 175.240312, nodeDeg: 323.025178, argpDeg: 128.796943,
    maDeg: 2636.10924, epochJd: 2461340.5, tpJd: 2460977.9418,
    solution: '2026-10-21', lastObs: '2026-10-16',
    hMag: null, // a comet's M1 (11.8) is not an asteroid's H, and nothing here reads it
    diameterKm: null, // "as small as 440 metres" and "upper limit 5.6 kilometres" are limits, not a size
    moons: null,
    why: 'The third object seen passing through the Solar System from another star, reported by the ATLAS survey in Chile on 1 July 2025. ' +
      'It came closest to the Sun, about 1.4 au, at the end of that October.',
    whySource: 'Wikipedia, 3I/ATLAS, and NASA Science, 3I/ATLAS, read 29 October 2026',
    departure: 'its nucleus is hidden in its cloud and nobody has seen it; the tails are part of the drawing, not an observation',
    orbitCaveat: 'where it is now is worked out from JPL’s orbit, not seen',
  },
  // 0079 a6: end
];

/**
 * The far-bodies layer's records. Not samples: cls 'inferred', a real phase, and no dashed halo.
 * @returns {Array<Object>} 11 records
 */
export function farBodies() {
  return FAR_BODIES.map((b) => sbdbBody(b, 'far-bodies'));
}

/** One JPL small-body row (FAR_BODIES, NAMED_ASTEROIDS) as a record of `layer`, with its phase. */
function sbdbBody(b, layer) {
  {
    const aKm = b.aAu * AU_KM; // negative on a hyperbola, which kepler.js expects
    const qKm = aKm * (1 - b.e);
    const epochMs = jdToMs(b.epochJd);
    const lastObsMs = Date.parse(b.lastObs);
    const bound = b.e < 1;
    return {
      id: b.id,
      name: b.name,
      layer,
      klass: b.klass,
      propagator: 'kepler',
      frame: 'sun-inertial',
      cls: 'inferred',
      // The age the card prints is the age of the EVIDENCE, as for the Roadster: the last
      // observation in JPL's fit, not the epoch the elements were re-osculated to.
      epoch: lastObsMs,
      source: 'jpl-sbdb',
      elements: {
        qKm,
        e: b.e,
        aKm,
        iRad: b.iDeg * DEG,
        omRad: b.nodeDeg * DEG,
        wRad: b.argpDeg * DEG,
        maRad: b.maDeg * DEG,
        epochMs,
        muKm3S2: MU_SUN,
      },
      meta: {
        designation: b.designation,
        aliases: b.aliases.slice(),
        farKind: b.farKind,
        aAu: b.aAu,
        qAu: qKm / AU_KM,
        aphelionAu: bound ? b.aAu * (1 + b.e) : null,
        eccentricity: b.e,
        inclinationDeg: b.iDeg,
        nodeDeg: b.nodeDeg,
        argpDeg: b.argpDeg,
        meanAnomalyDeg: b.maDeg,
        perihelionMs: jdToMs(b.tpJd),
        // JPL's heliocentric osculating period. See Sedna's row for why no card prints it.
        periodDays: bound ? periodDays(aKm) : null,
        absoluteMagnitude: b.hMag,
        diameterKm: b.diameterKm,
        diameterLowKm: b.diameterLowKm ?? null,
        diameterHighKm: b.diameterHighKm ?? null,
        moons: Array.isArray(b.moons) ? b.moons.slice() : null,
        // Never near Earth: the asteroid card's near-Earth sentence reads this, as for Vesta.
        neo: b.neo === true,
        why: b.why,
        whySource: b.whySource,
        // Noon UTC, so "6 June 2026" is 6 June in every time zone a visitor reads it from.
        cite: `JPL Small-Body Database, ${b.designation}: orbit solved ${timeText.longDate(Date.parse(`${b.solution}T12:00:00Z`))}, read 22 September 2026`,
        solution: b.solution,
        // The honesty clause's arc sentence is for a body nobody can see any more; the dwarf
        // planets are observed every season and their age line is enough.
        arcEnd: b.orbitCaveat ? `${b.lastObs}T12:00:00Z` : null,
        orbitCaveat: b.orbitCaveat || null,
        departure: b.departure || null,
        // scene/orbitline.js draws the whole orbit (or, on a hyperbola, the whole passage) for
        // these rather than the coming year, which is a sliver of a 560-year lap.
        wholePath: true,
      },
    };
  }
}

// =================================================================================================
// The asteroids a visitor asks for by name (2026-10-06)
// =================================================================================================
//
// Apophis, Bennu, Didymos, Vesta and Phaethon, each on JPL's orbit WITH ITS PHASE, so each is drawn
// where it is today: the same construction as the dwarf planets above, cls 'inferred'. They are
// rows of the `asteroids` layer ALWAYS, beside whatever JPL's close-approach table holds that
// week (data/layers.js `always:`). Until now they existed only as the stand-ins at the top of
// this file, parked at a placeholder perihelion and shown only when the snapshot was missing,
// which on the live site is never: a trip about Apophis flew to nothing (public #239).
//
//   Elements   https://ssd-api.jpl.nasa.gov/sbdb.api?sstr=<number>&phys-par=1&full-prec=1, read
//              2026-10-06, heliocentric ecliptic J2000, kept to ten significant figures. Bennu's
//              row says why it is Horizons' instead.
//   Checked    against JPL Horizons heliocentric vectors for 2026-10-06 00:00 TDB: two-body motion
//              from these elements lands within 0.0001 au for all five (each row has its number),
//              and tests/test_remaining_trips.mjs holds them there.
//   NOT        a close approach. Two-body motion knows nothing of the Earth's pull, so Apophis's
//              pass of 13 April 2029 cannot be flown from this ROW. Since 2026-10-06 the record
//              also has a file of its path from JPL Horizons, 2026 to 2029, kept relative to the
//              Earth round the pass (registry/ephemerides.yaml); once that file is here it
//              answers instead of the row (propagate/index.js EPHEMERIS_OF), and the trip
//              `asteroids-that-come-close` flies the pass from it.
/** @type {Array<Object>} the same columns as FAR_BODIES, with `neo` */
export const NAMED_ASTEROIDS = [
  {
    id: 'asteroid-99942',
    name: 'Apophis',
    designation: '99942 Apophis',
    aliases: ['99942 Apophis', '2004 MN4'],
    klass: 'asteroid',
    neo: true,
    // sstr=99942. Orbit 220, solved 2024-06-25, observations 2004-03-15 to 2022-04-09.
    // Against Horizons on 2026-10-06 00:00 TDB: 5e-05 au off, at 0.839 au from the Sun.
    aAu: 0.9223592207, e: 0.191149228, iDeg: 3.34099688, nodeDeg: 203.8936514, argpDeg: 126.6795707,
    maDeg: 175.3304027, epochJd: 2461200.5, tpJd: 2461042.9192,
    solution: '2024-06-25', lastObs: '2022-04-09',
    hMag: 19.09,
    diameterKm: 0.34,
    moons: null,
    why: "A rock about 340 metres across that will pass about 32 000 km above the ground on 13 April 2029, closer than the geostationary satellites. It will miss. Set the clock to that evening and it is drawn on JPL's predicted path.",
    whySource: "NASA Science, Apophis (science.nasa.gov/solar-system/asteroids/apophis) and the JPL Small-Body Database (size), read 6 October 2026",
  },
  {
    id: 'asteroid-101955',
    name: 'Bennu',
    designation: '101955 Bennu',
    aliases: ['101955 Bennu', '1999 RQ36'],
    klass: 'asteroid',
    neo: true,
    // sstr=101955. Orbit 118, solved 2021-01-07, observations 1999-09-11 to 2020-10-03.
    // The SBDB states this orbit at a 2011 epoch (two-body motion from there misses Horizons by 0.042 au
    // today), so these are JPL HORIZONS' osculating elements of the same solution at JD 2461200.5
    // (EPHEM_TYPE=ELEMENTS, CENTER=500@10, ecliptic J2000).
    // Against Horizons on 2026-10-06 00:00 TDB: 5e-05 au off, at 1.353 au from the Sun.
    aAu: 1.125950726, e: 0.2036821439, iDeg: 6.032966275, nodeDeg: 1.966574037, argpDeg: 66.41055452,
    maDeg: 72.45176655, epochJd: 2461200.5, tpJd: 2461112.67382,
    solution: '2021-01-07', lastObs: '2020-10-03',
    hMag: 20.21,
    diameterKm: 0.484,
    moons: null,
    why: "About half a kilometre wide. NASA’s OSIRIS-REx collected 121.6 grams of it and brought them back to the Utah desert on 24 September 2023, the largest asteroid sample ever collected in space.",
    whySource: "NASA Science, OSIRIS-REx (science.nasa.gov/mission/osiris-rex) and the JPL Small-Body Database (size, Daly et al. 2020), read 6 October 2026",
  },
  {
    id: 'asteroid-65803',
    name: 'Didymos',
    designation: '65803 Didymos',
    aliases: ['65803 Didymos', '1996 GT', 'Dimorphos'],
    klass: 'asteroid',
    neo: true,
    // sstr=65803. Orbit 240, solved 2026-03-01, observations 2022-09-27 to 2025-03-11.
    // Against Horizons on 2026-10-06 00:00 TDB: 3e-05 au off, at 1.357 au from the Sun.
    aAu: 1.642709609, e: 0.3831233243, iDeg: 3.413876519, nodeDeg: 72.98582362, argpDeg: 319.5807001,
    maDeg: 260.8612886, epochJd: 2461200.5, tpJd: 2461412.27778,
    solution: '2026-03-01', lastObs: '2025-03-11',
    hMag: 18.11,
    diameterKm: 0.78,
    moons: null,
    why: "An asteroid 780 metres across with a small moon, Dimorphos. NASA’s DART was flown into the moon in September 2022 and shortened its orbit by 32 minutes. ESA’s Hera is due there in November 2026.",
    whySource: "NASA Science, DART (science.nasa.gov/planetary-defense-dart), ESA, Hera (esa.int/Space_Safety/Hera) and the JPL Small-Body Database (size, Naidu et al. 2020), read 6 October 2026",
  },
  {
    id: 'asteroid-4',
    name: 'Vesta',
    designation: '4 Vesta',
    aliases: ['4 Vesta', 'A807 FA'],
    klass: 'asteroid',
    neo: false,
    // sstr=4. Orbit 36, solved 2021-04-13, observations 1950-09-23 to 2021-03-17.
    // Against Horizons on 2026-10-06 00:00 TDB: 2e-05 au off, at 2.462 au from the Sun.
    aAu: 2.361365965, e: 0.09020374383, iDeg: 7.143925545, nodeDeg: 103.7012933, argpDeg: 151.4686478,
    maDeg: 81.19015608, epochJd: 2461200.5, tpJd: 2460901.58738,
    solution: '2021-04-13', lastObs: '2021-03-17',
    hMag: 3.25,
    diameterKm: 522.8,
    moons: null,
    why: "The second most massive body in the asteroid belt, 523 km across. NASA’s Dawn orbited it from July 2011 to September 2012, and three groups of meteorites found on Earth appear to come from it.",
    whySource: "NASA Science, 4 Vesta (science.nasa.gov/solar-system/asteroids/4-vesta) and the JPL Small-Body Database (size, Park et al. 2025), read 6 October 2026",
  },
  {
    id: 'asteroid-3200',
    name: 'Phaethon',
    designation: '3200 Phaethon',
    aliases: ['3200 Phaethon', '1983 TB'],
    klass: 'asteroid',
    neo: true,
    // sstr=3200. Orbit 1007, solved 2026-10-05, observations 1983-10-27 to 2026-10-04.
    // Against Horizons on 2026-10-06 00:00 TDB: 4e-05 au off, at 0.916 au from the Sun.
    aAu: 1.271464621, e: 0.8896722844, iDeg: 22.31052728, nodeDeg: 265.098806, argpDeg: 322.3001685,
    maDeg: 301.4858235, epochJd: 2461200.5, tpJd: 2461285.61644,
    solution: '2026-10-05', lastObs: '2026-10-04',
    hMag: 14.38,
    diameterKm: 6.25,
    moons: null,
    why: "The Geminid meteors of December come from this one. Its path takes it closer to the Sun than Mercury’s does, and nobody is sure whether it is a rock or a dead comet.",
    whySource: "NASA Science, Geminids (science.nasa.gov/solar-system/meteors-meteorites/geminids) and the JPL Small-Body Database (orbit, and the size, Taylor et al. 2019), read 6 October 2026",
  },
  // 0077 W3-d: begin -- RYUGU AND ITOKAWA (release 1.3.0 stage W3-d, internal #866), the two asteroids Japan
  // brought pieces of home, rows of the layer like the five above since they are drawn in their measured shapes
  // (scene/realmodels.js). Until now they were only the stand-ins at the top of this file, never on the live map.
  // Elements: https://ssd-api.jpl.nasa.gov/sbdb.api?sstr=<number>&phys-par=1&full-prec=1, read 2026-10-20,
  // ecliptic J2000 at the same epoch as the rows above. Against JPL Horizons on 2026-10-06 00:00 TDB (heliocentric
  // vectors, ecliptic J2000; tests/test_remaining_trips.mjs holds them).
  {
    id: 'asteroid-162173',
    name: 'Ryugu',
    designation: '162173 Ryugu',
    aliases: ['162173 Ryugu', '1999 JU3'],
    klass: 'asteroid',
    neo: true,
    // sstr=162173. Orbit 270, solved 2026-08-11, observations to 2025-07-23.
    aAu: 1.190918933, e: 0.1910730046, iDeg: 5.866442486, nodeDeg: 251.2897124, argpDeg: 211.6089938,
    maDeg: 62.34067409, epochJd: 2461200.5, tpJd: 2461118.29642,
    solution: '2026-08-11', lastObs: '2025-07-23',
    hMag: 19.55,
    diameterKm: 0.896,
    moons: null,
    why: "A dark rock 900 metres across, shaped like a spinning top. JAXA’s Hayabusa2 shot a copper slug into it, touched down twice and brought 5.4 grams of it to Earth in December 2020.",
    whySource: "JAXA, Hayabusa2 (hayabusa2.jaxa.jp/en) and the JPL Small-Body Database (size, shape and spin: Watanabe et al. 2019; pole: Preusker et al. 2019), read 20 October 2026",
  },
  {
    id: 'asteroid-25143',
    name: 'Itokawa',
    designation: '25143 Itokawa',
    aliases: ['25143 Itokawa', '1998 SF36'],
    klass: 'asteroid',
    neo: true,
    // sstr=25143. Orbit 237, solved 2025-05-22, observations to 2024-12-19.
    aAu: 1.324052284, e: 0.2801776415, iDeg: 1.620940811, nodeDeg: 69.0744975, argpDeg: 162.8409022,
    maDeg: 170.6539059, epochJd: 2461200.5, tpJd: 2460936.70299,
    solution: '2025-05-22', lastObs: '2024-12-19',
    hMag: 19.26,
    diameterKm: 0.33,
    moons: null,
    why: "A rubble pile 535 metres long, two lumps of loose rock touching. JAXA’s Hayabusa landed on it in 2005 and brought about 1 500 grains of its dust home in 2010, the first pieces of an asteroid ever returned.",
    whySource: "JAXA, Hayabusa (global.jaxa.jp/projects/sas/hayabusa) and the JPL Small-Body Database (size and shape: Fujiwara et al. 2006; pole: Demura et al. 2006), read 20 October 2026",
  },
  // 0077 W3-d: end
  // 0079 B1: begin -- THE SMALL BODIES A SPACECRAFT OR RADAR HAS SHAPED (release 1.5.0 stage B1, internal #1065): Lutetia, Steins,
  // Donaldjohanson, Arrokoth and the radar-shaped Toutatis, Geographos, Kleopatra, Mithra, Golevka and 1996 HW1, each a row of the
  // layer like the seven above since each is drawn in its measured shape (scene/realmodels.js). Elements: JPL SBDB at full
  // precision (scripts/fetch-sbdb-rows.mjs, read 2026-10-28), epoch JD 2461200.5, ecliptic J2000; each is held to JPL Horizons
  // for 2026-10-06 00:00 TDB by tests/test_remaining_trips.mjs. Toutatis's diameterKm is the geometric mean of its three radar axes
  // (1.70 x 2.03 x 4.26 km); the SBDB's own 5.4 km is nearer its length.
  {
    id: 'asteroid-21',
    name: 'Lutetia',
    designation: '21 Lutetia',
    aliases: ['21 Lutetia', 'A852 VA'],
    klass: 'asteroid',
    neo: false,
    // sstr=21. Orbit 146, solved 2026-06-05, observations 1866-04-06 to 2026-03-30.
    aAu: 2.434430743, e: 0.1647703512, iDeg: 3.064452891, nodeDeg: 80.83855721, argpDeg: 249.8802781,
    maDeg: 298.7726415, epochJd: 2461200.5, tpJd: 2461436.4596,
    solution: '2026-06-05', lastObs: '2026-03-30',
    hMag: 7.48,
    diameterKm: 98,
    moons: null,
    why: "A dense rock 121 kilometres long that ESA’s Rosetta passed at 3 168 kilometres on 10 July 2010, on its way to a comet. Rosetta’s pictures covered more than half of it; the rest of the shape is fitted from its changes in brightness.",
    whySource: "NASA Planetary Data System, Rosetta shape model of 21 Lutetia (dataset RO-A-OSINAC/OSIWAC-5-LUTETIA-SHAPE-V1.0) and the JPL Small-Body Database (size and density: Sierks et al. 2011; Pätzold et al. 2011), read 28 October 2026",
  },
  {
    id: 'asteroid-2867',
    name: 'Steins',
    designation: '2867 Steins',
    aliases: ['2867 Steins', '1969 VC'],
    klass: 'asteroid',
    neo: false,
    // sstr=2867. Orbit 97, solved 2026-06-05, observations 1951-11-28 to 2026-04-13.
    aAu: 2.363141777, e: 0.1467867767, iDeg: 9.924748539, nodeDeg: 55.29361895, argpDeg: 251.437122,
    maDeg: 275.4028858, epochJd: 2461200.5, tpJd: 2461512.3072,
    solution: '2026-06-05', lastObs: '2026-04-13',
    hMag: 13.13,
    diameterKm: 5.16,
    moons: null,
    why: "A bright rock 6.8 kilometres long that ESA’s Rosetta passed at 803 kilometres on 5 September 2008. Its prime meridian runs through the crater Spinel. Rosetta’s pictures covered about 60 percent of it; the rest of the shape is fitted from its changes in brightness.",
    whySource: "NASA Planetary Data System, Rosetta shape model of 2867 Steins (dataset RO-A-OSINAC/OSIWAC-5-STEINS-SHAPE-V1.0; Jorda et al. 2012) and the JPL Small-Body Database (size), read 28 October 2026",
  },
  {
    id: 'asteroid-52246',
    name: 'Donaldjohanson',
    designation: '52246 Donaldjohanson',
    aliases: ['52246 Donaldjohanson', '1981 EQ5'],
    klass: 'asteroid',
    neo: false,
    // sstr=52246. Orbit 22, solved 2024-09-11, observations 2001-08-11 to 2024-09-01.
    aAu: 2.383835831, e: 0.1868593763, iDeg: 4.42520524, nodeDeg: 262.7765342, argpDeg: 212.8821499,
    maDeg: 147.852589, epochJd: 2461200.5, tpJd: 2460648.3725,
    solution: '2024-09-11', lastObs: '2024-09-01',
    hMag: 15.61,
    diameterKm: 3.895,
    moons: null,
    why: "A main-belt rock shaped like two lobes joined at a narrow neck, named for the discoverer of the Lucy fossil, which the mission is named for. NASA’s Lucy flew past it in April 2025; the shape is DLR’s, from Lucy’s pictures.",
    whySource: "NASA Navigation and Ancillary Information Facility, Lucy SPICE bundle (shape kernel lcy_donj_k548_iso20m_v10, DLR; constants donaldjohanson_v12.tpc) and the JPL Small-Body Database (size), read 28 October 2026",
  },
  {
    id: 'asteroid-4179',
    name: 'Toutatis',
    designation: '4179 Toutatis',
    aliases: ['4179 Toutatis', '1989 AC'],
    klass: 'asteroid',
    neo: true,
    // sstr=4179. Orbit 726, solved 2026-06-16, observations 1934-02-10 to 2026-05-16.
    aAu: 2.543047156, e: 0.6246302247, iDeg: 0.4480836625, nodeDeg: 125.36548, argpDeg: 277.8615384,
    maDeg: 125.5161576, epochJd: 2461200.5, tpJd: 2460684.0513,
    solution: '2026-06-16', lastObs: '2026-05-16',
    hMag: 15.29,
    diameterKm: 2.5,
    moons: null,
    why: "A rock 4.6 kilometres long that tumbles, turning about two axes at once, instead of spinning like a top. Its shape is from radar echoes. China’s Chang’e 2 flew past it in December 2012.",
    whySource: "NASA 3D Resources, Asteroid 4179 Toutatis (made from the Goldstone radar shape model; github.com/nasa/NASA-3D-Resources) and the JPL Small-Body Database (size and rotation), read 28 October 2026",
  },
  {
    id: 'asteroid-1620',
    name: 'Geographos',
    designation: '1620 Geographos',
    aliases: ['1620 Geographos', '1951 RA'],
    klass: 'asteroid',
    neo: true,
    // sstr=1620. Orbit 965, solved 2026-10-28, observations 1951-09-14 to 2026-10-27.
    aAu: 1.245803623, e: 0.3355177776, iDeg: 13.33675506, nodeDeg: 337.1348663, argpDeg: 277.0290684,
    maDeg: 354.6795027, epochJd: 2461200.5, tpJd: 2461208.0062,
    solution: '2026-10-28', lastObs: '2026-10-27',
    hMag: 15.27,
    diameterKm: 2.56,
    moons: null,
    why: "A rock about 5 kilometres long and 2 wide that turns once in 5.2 hours. Its shape is from radar echoes.",
    whySource: "NASA 3D Resources, Asteroid 1620 Geographos (made from the radar shape model) and the JPL Small-Body Database (size and rotation), read 28 October 2026",
  },
  {
    id: 'asteroid-216',
    name: 'Kleopatra',
    designation: '216 Kleopatra',
    aliases: ['216 Kleopatra', 'A880 GB'],
    klass: 'asteroid',
    neo: false,
    // sstr=216. Orbit 157, solved 2026-06-05, observations 1880-04-20 to 2026-04-14.
    aAu: 2.795272753, e: 0.2501676833, iDeg: 13.11552665, nodeDeg: 215.3098462, argpDeg: 179.7697642,
    maDeg: 259.8567077, epochJd: 2461200.5, tpJd: 2461675.3474,
    solution: '2026-06-05', lastObs: '2026-04-14',
    hMag: 7.02,
    diameterKm: 122,
    moons: null,
    why: "A main-belt metal-rich asteroid shaped like a dog bone, 276 kilometres from end to end. It has two small moons, Alexhelios and Cleoselene. Its shape is from radar echoes.",
    whySource: "NASA 3D Resources, Asteroid 216 Kleopatra (made from the radar shape model) and the JPL Small-Body Database (size, spectral type), read 28 October 2026",
  },
  {
    id: 'asteroid-4486',
    name: 'Mithra',
    designation: '4486 Mithra',
    aliases: ['4486 Mithra', '1987 SB'],
    klass: 'asteroid',
    neo: true,
    // sstr=4486. Orbit 414, solved 2026-10-25, observations 1987-01-29 to 2026-10-24.
    aAu: 2.205244726, e: 0.6598497281, iDeg: 3.029603398, nodeDeg: 82.16376805, argpDeg: 169.3002224,
    maDeg: 336.4031483, epochJd: 2461200.5, tpJd: 2461278.9035,
    solution: '2026-10-25', lastObs: '2026-10-24',
    hMag: 15.62,
    diameterKm: 1.849,
    moons: null,
    why: "A near-Earth rock about 2 kilometres long that turns once in 67 hours. Its shape is from radar echoes.",
    whySource: "NASA 3D Resources, Asteroid 4486 Mithra (made from the radar shape model) and the JPL Small-Body Database (size and rotation), read 28 October 2026",
  },
  {
    id: 'asteroid-6489',
    name: 'Golevka',
    designation: '6489 Golevka',
    aliases: ['6489 Golevka', '1991 JX'],
    klass: 'asteroid',
    neo: true,
    // sstr=6489. Orbit 107, solved 2022-12-20, observations 1991-04-15 to 2015-11-03.
    aAu: 2.474378474, e: 0.6190939113, iDeg: 2.260612192, nodeDeg: 208.5519488, argpDeg: 69.78033485,
    maDeg: 302.9054901, epochJd: 2461200.5, tpJd: 2461425.9704,
    solution: '2022-12-20', lastObs: '2015-11-03',
    hMag: 19.22,
    diameterKm: 0.53,
    moons: null,
    why: "A rock about half a kilometre across. Radar measured how the push of sunlight on it is changing its orbit, the first time that was done for an asteroid. Its shape is from radar echoes.",
    whySource: "NASA 3D Resources, Asteroid 6489 Golevka (made from the radar shape model) and the JPL Small-Body Database (size), read 28 October 2026",
  },
  {
    id: 'asteroid-8567',
    name: '1996 HW1',
    designation: '(1996 HW1)',
    aliases: ['8567', '(1996 HW1)', '1996 HW1'],
    klass: 'asteroid',
    neo: true,
    // sstr=8567. Orbit 620, solved 2026-10-03, observations 1955-05-23 to 2026-10-02.
    aAu: 2.045539429, e: 0.4490435145, iDeg: 8.449304858, nodeDeg: 177.0887379, argpDeg: 177.2827715,
    maDeg: 21.69559114, epochJd: 2461200.5, tpJd: 2461136.101,
    solution: '2026-10-03', lastObs: '2026-10-02',
    hMag: 15.36,
    diameterKm: 2.928,
    moons: null,
    why: "A near-Earth rock made of two lobes touching, found by radar to be a contact binary. Its shape is from radar echoes.",
    whySource: "NASA 3D Resources, Asteroid 8567 1996 HW1 (made from the radar shape model) and the JPL Small-Body Database (size), read 28 October 2026",
  },
  {
    id: 'asteroid-486958',
    name: 'Arrokoth',
    designation: '486958 Arrokoth',
    aliases: ['486958 Arrokoth', '2014 MU69', 'Ultima Thule', 'MU69'],
    klass: 'asteroid',
    neo: false,
    // sstr=486958. Orbit 3, solved 2024-02-21, observations 2014-06-26 to 2018-08-04.
    aAu: 44.05257836, e: 0.03555717645, iDeg: 2.450613925, nodeDeg: 159.0377268, argpDeg: 188.8507463,
    maDeg: 310.983931, epochJd: 2461200.5, tpJd: 2475741.4039,
    solution: '2024-02-21', lastObs: '2018-08-04',
    hMag: 11.06,
    diameterKm: 18,
    moons: null,
    why: "Two lumps of ice and rock joined at a neck, 35 kilometres from end to end, in the Kuiper belt more than 40 times farther from the Sun than the Earth. NASA’s New Horizons flew past it on 1 January 2019, the most distant object ever visited. The side it did not see is inferred.",
    whySource: "NASA Planetary Data System, New Horizons Arrokoth shape model and albedo map (Porter et al. 2024, PDS4 collection nh_derived:arrokoth_shapemodel_porter2024) and the JPL Small-Body Database (orbit), read 28 October 2026",
  },
  // 0079 B1: end
  // 0079 a6: begin -- Tianwen-2's target. SBDB sstr=469219, read 2026-10-29 through a fetch tool (this laptop's TLS refuses JPL): orbit solved 2026-09-03 from
  // 335 observations 2004-03-17 to 2025-03-07, at JD 2461200.5; a, e and q agree (q = a(1 - e)), the period is a^1.5 years, and the mean anomaly is the mean motion
  // times the time to JPL's perihelion, tp, so the nine figures are one coherent solution.
  {
    id: 'asteroid-469219',
    name: 'Kamoʻoalewa',
    designation: '469219 Kamoʻoalewa',
    aliases: ['469219 Kamoʻoalewa', '469219', '2016 HO3', 'Kamooalewa', 'Kamo`oalewa', 'Tianwen-2 target'],
    klass: 'asteroid',
    neo: true,
    aAu: 1.000810462, e: 0.1022388435, iDeg: 7.802614369, nodeDeg: 65.59324200, argpDeg: 304.3632128,
    maDeg: 243.3871442, epochJd: 2461200.5, tpJd: 2461318.9596,
    solution: '2026-09-03', lastObs: '2025-03-07',
    hMag: 24.31,
    diameterKm: null, // no measured size: the H magnitude gives a range, and Wikipedia's figure is an estimate
    moons: null,
    why: 'A very small asteroid that goes round the Sun in step with the Earth, so it stays near us: a quasi-satellite. China’s Tianwen-2 reached it in July 2026 and is looking for a place to take a sample.',
    whySource: 'Wikipedia, 469219 Kamoʻoalewa and Tianwen-2, read 29 October 2026',
  },
  // 0079 a6: end
];

// HALLEY, for the same reason: the comets layer is JPL's snapshot or nothing, and the one comet
// everybody knows is far outside what that table selects (it is 35 au out, past Neptune, and
// will not be back until 2061). JPL Horizons' osculating elements of solution JPL#75 at
// JD 2461200.5 (COMMAND='DES=1P;CAP', EPHEM_TYPE=ELEMENTS, CENTER=500@10, ecliptic J2000; read
// 2026-10-06); two-body motion from them is within 0.0001 au of Horizons' own vector for
// 2026-10-06 (tests/test_remaining_trips.mjs). JPL's solution 75 was made on 2025-11-21 from
// observations of 1835 to 1994. The nucleus: 11 km across (Lamy et al. 2004, Comets II), 14.9 x
// 8.2 km as Giotto saw it (Keller et al. 1987, A&A 187, 807), both from the SBDB. The tail scene/models.js gives every selected comet is a
// drawing; at this distance the real one has none, and the row's `departure` line says so.
const NAMED_COMETS = [
  {
    id: 'comet-1P',
    name: '1P/Halley',
    designation: '1P',
    aliases: ['Halley', 'Halley\u2019s Comet', 'Comet Halley', '1P'],
    klass: 'comet',
    neo: false,
    aAu: 17.85907724, e: 0.9680203461, iDeg: 162.1787514, nodeDeg: 59.36033415, argpDeg: 112.2600251,
    maDeg: 192.2751783, epochJd: 2461200.5, tpJd: 2474043.95264,
    solution: '2025-11-21', lastObs: '1994-01-11',
    hMag: 5.5,
    diameterKm: 11,
    moons: null,
    why: "The comet everybody has heard of. It comes round every 76 years on average, was last near the Sun in 1986, and is now out past Neptune. It returns in 2061. Its dust makes two meteor showers a year, in May and in October.",
    whySource: "NASA Science, 1P/Halley (science.nasa.gov/solar-system/comets/1p-halley), and JPL Horizons for where it is, read 6 October 2026",
    departure: 'the tail is part of the drawing: this far from the Sun the comet is a dark, frozen lump with no tail at all',
  },
  // 0079 B1: begin -- 67P, the comet Rosetta orbited (internal #1065), in the SHAP5 shape model
  {
    id: 'comet-67P',
    name: '67P/Churyumov-Gerasimenko',
    designation: '67P',
    aliases: ['67P', 'Churyumov-Gerasimenko', 'Comet 67P', 'Rosetta’s comet'],
    klass: 'comet',
    neo: false,
    // JPL Horizons' osculating elements of solution K284/1 at JD 2461200.5 (COMMAND='DES=67P;CAP', EPHEM_TYPE=ELEMENTS, CENTER=500@10,
    // ecliptic J2000; read 2026-10-28): the SBDB states 67P's orbit at its 2015 perihelion, too old to carry two-body motion to today
    // (Halley's row above is Horizons' for the same reason).
    aAu: 3.458885922, e: 0.649561941, iDeg: 3.866423389, nodeDeg: 36.293625, argpDeg: 22.23542571,
    maDeg: 257.2702553, epochJd: 2461200.5, tpJd: 2461870.9965,
    solution: '2026-09-04', lastObs: '2025-06-21',
    hMag: null, // the SBDB states none for a comet; the app ranks it by the distance it is at
    diameterKm: 4.1,
    moons: null,
    why: "The comet ESA’s Rosetta orbited for two years, from 2014 to 2016, and set its lander Philae down on 12 November 2014. Two lumps joined at a neck, 4.1 kilometres across the long way.",
    whySource: "NASA Planetary Data System and ESA, Rosetta shape model of 67P (SHAP5, Gaskell and Jorda; dataset RO-C-MULTI-5-67P-SHAPE-V2.0) and the JPL Small-Body Database (size and pole: Sierks et al. 2015), read 28 October 2026",
    departure: 'the tail is part of the drawing: this far from the Sun the comet is a dark, frozen lump with no tail at all',
  },
  // 0079 B1: end
];

/** The comets layer's always-present row: Halley, where it is today. */
export function namedComets() {
  return NAMED_COMETS.map((b) => sbdbBody(b, 'comets'));
}

/** The asteroids layer's always-present rows: five famous ones, each where it is today. */
export function namedAsteroids() {
  return NAMED_ASTEROIDS.map((b) => sbdbBody(b, 'asteroids'));
}

// =================================================================================================
// Deep space
// =================================================================================================
//
// Two honest constructions, and each record says which one it used.
//
// (A) ANCHORED. A craft holding station at a Sun–Earth Lagrange point is drawn on Earth's own
//     heliocentric orbit. The elements are the published J2000 Keplerian set for Earth — mean
//     anomaly included — so the position is genuinely right to about an arcminute. The offset
//     from Earth (1.5 million km to L1 or L2, 0.01 au) is smaller than the dot at the scale where
//     the Sun is the centre. Craft ORBITING a planet used to be drawn this way too; since
//     2026-09-22 they are (D) below, because at their own planet's scale the anchor is a lie.
//
// (B) STRAIGHT LINE. A craft far outside the planets is drawn from a distance, a speed and an
//     escape direction, all rounded, moving in a straight line. At 60 au the Sun bends the path
//     by well under a degree a decade, so the line is the honest shape. The three numbers are
//     stated in meta and each is soft.
//
// (C) OSCULATING. A craft on its own path round the Sun, between flybys, is drawn on the orbit
//     JPL Horizons gives for it on a stated day -- all six elements, mean anomaly included -- and
//     moved on by two-body motion. At that day it sits on Horizons' own position; after it, it
//     drifts by whatever the engines and the planets do, and each row says by how much, measured
//     against Horizons' own forecast (added 2026-09-22, OSCULATING_CRAFT below).
//
// (D) ROUND A WORLD. A craft in orbit round Mars, Jupiter or the Moon is kept in THAT world's
//     frame (`mars-inertial` and so on: centred on it, ecliptic J2000 axes) and drawn from it,
//     so it is exactly as far from the drawn world as the real craft is from the real one. The
//     stand-in is one JPL Horizons state relative to the world, moved on by the world's pull
//     alone (propagate/orbiter.js says why, with the measurements). PLANET_ORBITERS below.
//
// Parker Solar Probe and Solar Orbiter use a fifth, older shape: elements without a phase
// (ELLIPTIC_CRAFT). Every construction is only the stand-in: once the harvester's Horizons
// snapshot holds a craft's id, data/parsers.js parseHorizonsVectors() swaps the position for the
// real vectors and keeps everything else on the row -- for (D), only vectors relative to the
// craft's own world will do, and the harvester asks for those (harvest/lists/horizons-ids.yaml).

/** Published J2000 elements (JPL's approximate-positions table). L and varpi reduced to M and w. */
const ANCHORS = {
  earth: {
    aAu: 1.00000261,
    e: 0.01671123,
    iDeg: 0.0,
    nodeDeg: 0.0,
    argpDeg: 102.93768,
    m0Deg: 357.52689,
    label: "Earth's orbit",
    // MEASURED against astronomy-engine for 2026-09-06: this element set puts the record
    // 1.13 million km from Earth's true position — the same order as the 1.5 million km to a
    // Lagrange point, so the construction is as good as the thing it stands in for.
    driftNote: 'about a million kilometres, which is the same order as the distance to L1 and L2',
  },
};

// `earthRangeKm`: how far the craft really is from Earth, for the two held at a Sun–Earth
// Lagrange point. The drawing puts them on Earth's orbit (construction A above), so any distance
// FROM EARTH computed off that drawing measures the construction, not the spacecraft. Checked
// 2026-09-22 against Horizons (VECTORS, CENTER='500@399', 00:00 TDB): JWST 1 234 799 km, SOHO
// 1 637 602 km, so "1.5 million" is the right round figure for both.
//
// Gaia was the third, until that same check: Horizons -139479 (header revised 2025-05-16) says it
// "left its orbit around Earth-Sun L2 and was placed in a heliocentric disposal orbit" on
// 2025-03-28, and on 2026-09-22 it was 83 714 436 km from Earth. The card said "Also at Sun–Earth
// L2" and printed 1.5 million km. It is an osculating row now (OSCULATING_CRAFT).
//
// LAUNCH_DATES_READ. Every craft below carries `launched`, the UTC day it lifted off, read from each
// one's Wikipedia infobox ("Launch date", UTC) on 2026-10-01 and checked for the four whose local
// date differs (Solar Orbiter, BepiColombo and STEREO-A launched the evening before in local time;
// Hope the morning after in Japan). The card prints the days since (ui/cards.js, spec 0061 §4: a
// craft beyond Earth leads with its distance, its speed and its days since launch).
const ANCHORED_CRAFT = [
  {
    id: 'deep-jwst',
    name: 'James Webb Space Telescope',
    launched: '2021-12-25',
    klass: 'telescope',
    horizonsId: -170,
    anchor: 'earth',
    earthRangeKm: 1.5e6,
    note: 'A 6.5-metre gold mirror behind a sunshield the size of a tennis court, kept in the ' +
      'dark 1.5 million km behind Earth.',
    offset: 'It orbits the Sun–Earth L2 point, 1.5 million km beyond Earth — one hundredth of ' +
      "the way to the Sun. It is drawn here on Earth's orbit; the offset is smaller than the dot.",
  },
  {
    id: 'deep-soho',
    name: 'SOHO',
    launched: '1995-12-02',
    klass: 'telescope',
    horizonsId: -21,
    anchor: 'earth',
    earthRangeKm: 1.5e6,
    // "Without a break" was not true: contact was lost from June to September 1998.
    note: 'It has watched the Sun since 1995, and has found more than 5 000 comets falling ' +
      'into it along the way.',
    offset: 'It orbits the Sun–Earth L1 point, 1.5 million km SUNWARD of Earth. Drawn on ' +
      "Earth's orbit; the offset is smaller than the dot.",
  },
  // Added 2026-10-07 (internal #389, public #438). Horizons -211 header (revised 2026-09-30): "It
  // launched 2026-Aug-30 11:26:04 UTC on a Falcon Heavy from Kennedy Space Center (LC-39A) and
  // will orbit the Earth-Sun L2 point", a 2.4 m primary mirror. Horizons, VECTORS, CENTER='500@399':
  // 1 271 353 km from Earth on 2026-09-22 and 1 457 793 km on 2026-10-07. Its trajectory file is
  // the flight dynamics team's prediction and ends 2026-10-26; the harvester asks for it and the
  // anchor stays the stand-in when Horizons has nothing.
  {
    id: 'deep-roman',
    name: 'Nancy Grace Roman Space Telescope',
    launched: '2026-08-30',
    klass: 'telescope',
    horizonsId: -211,
    anchor: 'earth',
    earthRangeKm: 1.5e6,
    note: 'Launched in August 2026 with a mirror as wide as Hubble’s, 2.4 metres, to survey ' +
      'the sky in infrared light from beyond the Moon.',
    offset: 'It is on its way into an orbit of the Sun–Earth L2 point, 1.5 million km beyond Earth ' +
      '(JPL Horizons had it 1.46 million km out on 7 October 2026). It is drawn here on ' +
      "Earth's orbit; the offset is smaller than the dot.",
  },
];

/**
 * Construction D: craft in orbit round a world, drawn from that world (the block at the top of
 * this section, and propagate/orbiter.js for the measurements that forced it).
 *
 * `rKm`/`vKmS` are MEASURED: JPL Horizons, EPHEM_TYPE=VECTORS, CENTER='500@<world>' (499 Mars,
 * 599 Jupiter, 301 the Moon), default reference plane (ecliptic J2000, the frame `<world>-inertial`
 * uses), 2026-09-22 00:00 TDB, fetched 2026-09-22, to the metre and the millimetre per second.
 * The epoch is that instant in UTC, 69.184 s earlier (ORBITER_EPOCH_MS below): read as UTC, as the
 * heliocentric rows are, it would put MRO 235 km along its track from where JPL has it.
 *
 * Three sentences a card prints are on each row, each measured against Horizons' own positions
 * relative to the same world at 10-minute steps (queries as above, STEP_SIZE='10 m'):
 *   `orbit`  what IS known: how high, how often. The heights are the least and greatest distance
 *            from the world's centre over 2026-09-22..10-22 (MRO to 10-12, where its file ends),
 *            less the radius this app draws the world with (propagate/frames.js WORLD_RADIUS_KM).
 *   `drift`  how far the stand-in strays: this one state, moved on by the world's pull alone.
 *   `arc`    how close the harvested snapshot stays: JPL's states every six hours, and two-body
 *            arcs between them (data/parsers.js), worst case over the same month.
 * `periodMin` is the osculating period at the epoch (Horizons ELEMENTS, same centre and instant);
 * scene/orbitline.js draws one lap of it for the selection.
 */
const ORBITER_EPOCH_MS = Date.UTC(2026, 8, 22) - 69184;
const ORBITER_EPOCH_WORDS = '22 September 2026';

/**
 * GM of each world, km^3/s^2: the "Keplerian GM" Horizons prints in the ELEMENTS header for that
 * centre (read 2026-09-22). The same GM the elements the orbits were checked against were made with.
 */
const WORLD_GM = { mars: 42828.374857356255, jupiter: 126686531.9003704, moon: 4902.8001184575487 };
const WORLD_WORDS = { mars: 'Mars', jupiter: 'Jupiter', moon: 'the Moon' };

const PLANET_ORBITERS = [
  {
    id: 'deep-mro',
    name: 'Mars Reconnaissance Orbiter',
    launched: '2005-08-12',
    klass: 'probe',
    horizonsId: -74,
    world: 'mars',
    note: 'Its HiRISE camera can see something the size of a dinner table on Mars, and it ' +
      'relays much of what the rovers say.',
    rKm: [919.321, 803.949, 3481.344],
    vKmS: [-1.761825, 2.887384, -0.209889],
    periodMin: 111.6,
    // 3 626..3 695 km from Mars's centre, 92.7 degrees to Mars's equator (the orbit's pole against
    // Astronomy Engine's Mars axis). Horizons -74 header: "a 112 minute, near circular polar orbit".
    orbit: 'circles Mars over the poles every 112 minutes, about 240 to 310 km up',
    // 63 km at an hour, 1 577 at a day, 4 477 at three; more than a quarter-lap (90 degrees round
    // Mars) out from 3.6 days, and from a week on the error is the whole width of the orbit. Mars's
    // flattening turns an orbit this low faster than two-body motion can follow.
    drift: 'Checked against JPL’s own forecast, the dot is 1 600 km from MRO after a day, and ' +
      'after four days where MRO is on that ring is not known.',
    // 160 km (2026-09-23 21:00); 58 with 3-hourly states, 17 with hourly.
    arc: 'within about 160 km of JPL’s own track',
  },
  {
    id: 'deep-mars-express',
    name: 'Mars Express',
    launched: '2003-06-02',
    klass: 'probe',
    horizonsId: -41,
    world: 'mars',
    // Horizons -41 header (revised 2026-09-01): launched 2003-06-02 on a Soyuz-Fregat, Mars arrival
    // December 2003, ESA's. The stereo camera is HRSC, the High Resolution Stereo Camera (ESA's
    // Mars Express instrument pages).
    note: 'Europe’s first spacecraft to another planet, it has circled Mars since December 2003, ' +
      'mapping it in colour and in 3D with its stereo camera.',
    rKm: [1422.347, -1469.76, 3262.347],
    vKmS: [-0.524419, -3.980093, -1.115009],
    periodMin: 415.2,
    // 3 807..13 831 km from Mars's centre. The header's "Period: 6h 43m ... apocentre 11 560 km"
    // is an older orbit; these are the ones the file holds now.
    orbit: 'swings round Mars every 6 hours 55 minutes, from 420 km up out to 10 400 km',
    // Up to 2 859 km within the first day, a quarter-lap out from 2.0 days.
    drift: 'Checked against JPL’s own forecast, the dot drifts up to 3 000 km from it within a ' +
      'day, and after two days where it is on that orbit is not known.',
    // 88 km (2026-10-08 02:50).
    arc: 'within about 90 km of JPL’s own track',
  },
  // Added 2026-10-07 (internal #389). Horizons -53 header (revised 2026-09-30): launched
  // 2001-04-07 15:02 UTC on a Delta II, Mars arrival 2001-10-24, "has supported ongoing exploration
  // of Mars by relaying 95% of the data from the Mars rovers"; trajectory "fit to data through
  // 2026-Sep-23, prediction after that". Same queries as the rows above, on 2026-10-07.
  {
    id: 'deep-mars-odyssey',
    name: 'Mars Odyssey',
    launched: '2001-04-07',
    klass: 'probe',
    horizonsId: -53,
    world: 'mars',
    aliases: ['2001 Mars Odyssey'],
    note: 'In orbit since October 2001, longer than any other craft at another planet. It found ' +
      'water ice under the ground and relays what the rovers say.',
    rKm: [3664.181, -1037.886, -178.823],
    vKmS: [-0.293632, -0.372025, -3.312939],
    periodMin: 118.6,
    // 3 762..3 834 km from Mars's centre over 2026-09-22..10-22; 97.5 degrees to the ecliptic.
    orbit: 'circles Mars over the poles every 119 minutes, about 370 to 445 km up',
    // Worst within a day 63 km, within a week 362, within two weeks 690, within 30 days 1 533.
    drift: 'Checked against JPL’s own forecast, the dot stays within 100 km of it for a day and ' +
      'is about 700 km off after two weeks.',
    // 227 km (2026-10-04 15:00) from the nearer six-hourly state alone; the blend of the two is
    // no worse than that.
    arc: 'within about 250 km of JPL’s own track',
  },
  {
    id: 'deep-hope',
    name: 'Hope (Emirates Mars Mission)',
    launched: '2020-07-19',
    klass: 'probe',
    horizonsId: -62,
    world: 'mars',
    // Horizons -62 header (revised 2026-06-03): built by the UAE, Mars arrival 2021-02-09, a
    // 55-hour orbit "roughly 22000 x 44000 km", and the aim of "a global picture of how the martian
    // atmosphere varies throughout the day and year".
    note: 'Built by the United Arab Emirates, it has circled Mars every 55 hours since 2021, ' +
      'watching how the whole planet’s weather changes through the day.',
    rKm: [-13429.248, 32991.669, -4245.386],
    vKmS: [-0.896321, 0.107588, 0.594912],
    periodMin: 3332.6,
    // 23 271..47 001 km from Mars's centre.
    orbit: 'loops round Mars every 55 hours, 20 000 to 44 000 km up',
    // 3 km at a day, 166 at 30 days, 323 at 60 (Horizons' file ends 2026-11-26).
    drift: 'Checked against JPL’s own forecast, it stays within 200 km for a month and 350 km ' +
      'for two.',
    // 0.3 km.
    arc: 'within a kilometre of JPL’s own track',
  },
  {
    id: 'deep-juno',
    name: 'Juno',
    launched: '2011-08-05',
    klass: 'probe',
    horizonsId: -61,
    world: 'jupiter',
    // "Every 38 days" was one stage of an orbit the moon flybys kept shortening (53 days in the
    // prime mission, about 33 by 2024); "than anything has been" overlooked Galileo's probe,
    // which went in.
    note: 'It dives between Jupiter and its radiation belts every month or so, closer to the ' +
      'cloud tops than any orbiter before it.',
    rKm: [1350578.538, -661190.227, -5432547.724],
    vKmS: [0.553754, -0.858287, -0.963073],
    periodMin: 47084.5,
    // 80 325 km from Jupiter's centre at the 2026-10-11 perijove (10 414 above the 69 911 km this
    // app draws Jupiter with), 5 816 227 at apojove; perijoves 2026-09-09, 10-11 and 11-13
    // (Horizons, CENTER='500@599', hourly), 32.7 days apart.
    orbit: 'swings round Jupiter every 33 days, from 10 000 km above it out to 5.7 million km',
    // 84 km at a day, 1 194 at a week, and no more than 5 687 through the perijove of 2026-10-11
    // 22:59 TDB (10-minute steps). After it, Jupiter's pull at 80 000 km and the trim burn the header
    // puts four hours after every perijove change the orbit: 9 786 km six hours on, 22 354 on
    // 10-13, 71 832 by 10-22.
    drift: 'Checked against JPL’s own forecast, it stays within 6 000 km until its close pass ' +
      'of 11 October 2026, and is wrong after it.',
    // 902 km at 2026-10-11 21:08, two hours before that perijove, where the arcs are shortest in
    // time and longest in angle; 43 km from six to 24 hours out, and under 3 km more than a day
    // away.
    arc: 'within about 900 km of JPL’s own track in the hours round its closest pass, and within ' +
      '3 km more than a day from it',
  },
  {
    id: 'deep-lro',
    name: 'Lunar Reconnaissance Orbiter',
    launched: '2009-06-18',
    klass: 'probe',
    horizonsId: -85,
    world: 'moon',
    aliases: ['LRO'],
    // Horizons -85 header (revised 2026-08-05): launched 2009-06-18, extended mission in "a
    // low-maintenance orbit". Its camera (LROC) imaged the Apollo landing sites, and LROC post 1101
    // is the source registry/oddities.yaml gives for the Beresheet crash site this map draws.
    note: 'Circling the Moon since 2009, its camera has photographed the Apollo landers, and this ' +
      'map places the Beresheet crash from its pictures.',
    rKm: [-1673.335, -729.446, 131.352],
    vKmS: [-0.033981, -0.26336, -1.613845],
    periodMin: 116.8,
    // 1 801..1 853 km from the Moon's centre; 83 degrees to the lunar equator (the orbit's pole
    // against Astronomy Engine's lunar axis), so near-polar, not polar.
    orbit: 'circles the Moon every 117 minutes, about 65 to 115 km up',
    // 17 km at a day, 90 at two weeks, 606 at 30 days, 1 865 at 60, 2 878 at 90 (the orbit is
    // 3 650 km across, so by then where it is on the ring is lost).
    drift: 'Checked against JPL’s own forecast, the dot stays within 100 km of it for two weeks, ' +
      'and is about 600 km off after a month.',
    // 6 km.
    arc: 'within about 6 km of JPL’s own track',
  },
];

/**
 * distanceAu, speedKmS and the escape direction (heliocentric ecliptic J2000) are the published
 * figures, rounded. Direction is the escape asymptote and is good to a few degrees.
 */
const CRUISING_CRAFT = [
  {
    id: 'deep-voyager-1',
    name: 'Voyager 1',
    launched: '1977-09-05',
    klass: 'probe',
    horizonsId: -31,
    // MEASURED, and the evidence is here because this repo is code only and has no docs/ to
    // point at. JPL Horizons, COMMAND='-31', CENTER='500@10', 2026-09-07 00:00 TDB:
    // X=-32.13433, Y=-136.63593, Z=+98.85895 au -> r = 171.68 au. The row said 168 and cited a
    // check on 2026-09-06; 3.7 au is about a year of Voyager 1's travel, so the number was a
    // reading roughly a year older than the date beside it.
    distanceAu: 171.7,
    distanceMeasured: true,
    speedKmS: 17.0,
    lonDeg: 255,
    latDeg: 35,
    note: 'The most distant thing people have made. It left the Sun’s bubble in 2012 and ' +
      'its radio signal takes about a day to get here.',
  },
  {
    id: 'deep-voyager-2',
    name: 'Voyager 2',
    launched: '1977-08-20',
    klass: 'probe',
    horizonsId: -32,
    // Same query, COMMAND='-32': X=+39.83201, Y=-105.33488, Z=-89.54645 au -> r = 143.88 au.
    distanceAu: 143.9,
    distanceMeasured: true,
    speedKmS: 15.4,
    lonDeg: 290,
    latDeg: -34,
    note: 'The only spacecraft to have visited Uranus and Neptune, and it did both on the way ' +
      'out. It is leaving below the plane of the planets.',
  },
  {
    id: 'deep-new-horizons',
    name: 'New Horizons',
    launched: '2006-01-19',
    klass: 'probe',
    horizonsId: -98,
    // Same query, COMMAND='-98': X=+20.77359, Y=-62.10272, Z=+2.28371 au -> r = 65.52 au.
    distanceAu: 65.5,
    distanceMeasured: true,
    speedKmS: 13.6,
    lonDeg: 293,
    latDeg: -2,
    // "Still sending back" Arrokoth: that downlink finished in 2020. "A nine-year fall" was "nine
    // years of falling" until 2026-09-22, three characters over the 160 that
    // tests/test_deep_space.mjs now holds every note in this layer to.
    note: 'It crossed Pluto in nine hours in 2015 after a nine-year fall towards it, and ' +
      'in 2019 flew past Arrokoth, a Kuiper belt rock, the farthest thing ever visited.',
  },
  // The two Pioneers (2026-10-06): silent since 2003 and 1995, still coasting out, and drawn here
  // because each has a file of its whole path (registry/ephemerides.yaml). The three numbers are
  // read from that file, which is JPL Horizons' trajectory for -23 and -24, at 2026-10-06 00:00
  // UTC: Pioneer 10 at 141.96 au, 11.85 km/s, ecliptic longitude 80.2 and latitude +3.0 degrees;
  // Pioneer 11 at 117.98 au, 11.14 km/s, 284.7 and +13.9. JPL's header says of both
  // trajectories that they suit "general historical purposes", and the card of each says so.
  {
    id: 'deep-pioneer-10',
    name: 'Pioneer 10',
    launched: '1972-03-03',
    klass: 'probe',
    horizonsId: -23,
    distanceAu: 142.0,
    distanceMeasured: true,
    checkedWords: '6 October 2026',
    speedKmS: 11.9,
    lonDeg: 80,
    latDeg: 3,
    note: 'The first spacecraft through the asteroid belt and past Jupiter, in 1973. Its last ' +
      'signal was heard in January 2003.',
  },
  {
    id: 'deep-pioneer-11',
    name: 'Pioneer 11',
    launched: '1973-04-06',
    klass: 'probe',
    horizonsId: -24,
    distanceAu: 118.0,
    distanceMeasured: true,
    checkedWords: '6 October 2026',
    speedKmS: 11.1,
    lonDeg: 285,
    latDeg: 14,
    note: 'The first spacecraft to study Saturn up close, in 1979, after passing Jupiter. ' +
      'Last heard from in 1995.',
  },
];

/**
 * A CRAFT NOBODY HAS HEARD FROM (2026-10-08, internal #433, public #431): MAVEN. JPL Horizons'
 * header for -202 (revised Feb 13, 2026, read 2026-10-08): "There has been no contact with the
 * MAVEN spacecraft since December 6, 2025 with some evidence it experienced an unplanned delta-V
 * Dec. 4 and is spinning in an altered trajectory. Therefore predicts after December 4, 2025 may
 * be inaccurate."; trajectory "Fit to data through 2025-Dec-04, prediction thereafter"; launch
 * "Nov 18, 2013 @ 18:28 UTC", Mars arrival "September 22, 2014". Horizons answers nothing for it
 * after 2026-MAR-01, so the harvester cannot ask and this row is never replaced by a snapshot.
 * The state is Horizons' for 2025-12-04 00:00 UT (COMMAND='-202', CENTER='500@499', ecliptic
 * J2000, TIME_TYPE=UT), the last midnight inside the fit. From it: 5 726 km semi-major axis,
 * eccentricity 0.372, so 207 to 4 466 km above a 3 389.5 km Mars, a lap of 219.2 minutes.
 * It is classed `sample` and its `why` says what is and is not known, which is the line the card
 * prints (ui/cardfacts.js classLine).
 */
const LOST_ORBITER_EPOCH_MS = Date.UTC(2025, 11, 4);
const LOST_ORBITERS = [
  {
    id: 'deep-maven',
    name: 'MAVEN',
    launched: '2013-11-18',
    klass: 'probe',
    horizonsId: -202,
    world: 'mars',
    aliases: ['Mars Atmosphere and Volatile EvolutioN'],
    note: 'In orbit round Mars since September 2014 to study how the planet loses its air to ' +
      'space. NASA has not heard from it since 6 December 2025.',
    rKm: [1249.946451, 3646.897367, -6566.122126],
    vKmS: [1.106146210, 1.471414496, 0.617508591],
    periodMin: 219.2,
    orbit: 'swung round Mars every 3 hours 39 minutes, from about 200 km up out to about 4 500 km',
    why: 'NASA has had no contact with MAVEN since 6 December 2025. The orbit drawn is its last ' +
      'tracked one: when last tracked it swung round Mars every 3 hours 39 minutes, from about ' +
      '200 km up out to about 4 500 km. The dot starts from JPL Horizons’ position for it ' +
      'relative to Mars on 4 December 2025, the last day JPL’s track is fitted to tracking, and ' +
      'is carried round Mars by Mars’s pull alone. JPL says there is some evidence the craft was ' +
      'pushed onto an altered path that day. Where MAVEN is along that orbit today, and whether ' +
      'it is still on it, is not known.',
  },
];

/**
 * Construction E: ENDED MISSIONS (2026-10-06, internal #406, #389). Cassini was flown into Saturn
 * and Galileo into Jupiter, so today there is nowhere to draw them and this row draws nothing:
 * its own propagator has no samples and answers null. What it has instead is a file of its whole
 * path from JPL Horizons (registry/ephemerides.yaml, site/data/eph/<id>.bin), fetched when its
 * card opens; inside those years propagate/index.js draws it from the file, and its card lists
 * the dated events of its mission (registry/missions.yaml), each of which sets the clock.
 * `launched` and `ended` were read on NASA Science's page for each mission on 2026-10-06
 * (science.nasa.gov/mission/cassini, .../galileo), the pages registry/missions.yaml cites.
 */
const PAST_CRAFT = [
  {
    id: 'deep-cassini',
    name: 'Cassini',
    launched: '1997-10-15',
    ended: '2017-09-15',
    klass: 'probe',
    horizonsId: -82,
    aliases: ['Cassini-Huygens'],
    note: 'The first spacecraft to orbit Saturn, from 2004 to 2017. It carried the Huygens probe ' +
      'that landed on Titan, and ended by diving into the planet.',
    end: 'It was flown into Saturn on 15 September 2017',
  },
  {
    id: 'deep-galileo',
    name: 'Galileo',
    launched: '1989-10-18',
    ended: '2003-09-21',
    klass: 'probe',
    horizonsId: -77,
    note: 'It circled Jupiter 34 times from 1995 to 2003, dropped a probe into its atmosphere, ' +
      'and found signs of salt water under the ice of Europa.',
    end: 'It was flown into Jupiter on 21 September 2003',
  },
  // Mars 2020 in flight: the cruise that ended when Perseverance landed. The rover is the Jezero
  // site's record (registry/sites.yaml); this one exists so the months between the planets have
  // a spacecraft's card and not a place's. Dates: NSSDCA 2020-052A, read 2026-10-06, the page
  // registry/missions.yaml cites for Perseverance. No model: NASA's file is the rover, and the
  // rover flew folded inside an aeroshell, so the craft is drawn as the class's own shape.
  {
    id: 'deep-mars-2020',
    name: 'Mars 2020',
    launched: '2020-07-30',
    ended: '2021-02-18',
    klass: 'probe',
    horizonsId: -168,
    aliases: ['Perseverance cruise'],
    note: 'The spacecraft that carried the Perseverance rover and the Ingenuity helicopter from ' +
      'Florida to Mars, in seven months.',
    end: 'It reached Mars on 18 February 2021, and its rover is the Jezero site on this map',
  },
  // Dawn (2026-10-07, internal #389, public #431). NSSDCA 2007-043A, read 2026-10-07: launched
  // 27 September 2007 at 11:34 UT; "communications ended on 31 October 2018", and "after the end
  // of the mission Dawn remained in orbit around Ceres". JPL's reconstruction ends that day
  // (Horizons -203 header); what it prints after is a prediction it says does not model the
  // last events, so the craft is drawn only inside its tracked years.
  {
    id: 'deep-dawn',
    name: 'Dawn',
    launched: '2007-09-27',
    ended: '2018-10-31',
    klass: 'probe',
    horizonsId: -203,
    note: 'The first spacecraft to orbit two worlds beyond Earth: the asteroid Vesta in 2011 and ' +
      '2012, then the dwarf planet Ceres from 2015.',
    end: 'It fell silent on 31 October 2018, out of fuel, in an orbit of Ceres nobody has tracked since',
  },
  // Four more ended missions with their NASA meshes (2026-10-08, public #431). Dates as read that
  // day. Rosetta: JPL Horizons' header for -226 ("Launch (Ariane-5G)= Mar 2, 2004 07:17:51 UTC";
  // "On 2016-September-30 @ 10:39:28 UTC, the Rosetta spacecraft ended its mission by landing on
  // the comet"; orbit from 2014-Aug-6, Philae released November 12); NSSDCA's page for it did not
  // load. NEAR: NSSDCA 1996-008A (launch date 1996-02-17; "the mission ended with a touchdown in
  // the saddle region of Eros on 12 February 2001"; "the final contact was made on 28 February";
  // orbit insertion 14 February 2000). Stardust: NSSDCA 1999-003A (launch date 1999-02-07; coma of
  // Wild 2 entered 31 December 2003, closest 2 January 2004; capsule landed 15 January 2006; past
  // Tempel 1 on 15 February 2011 UT; "commanded into safe mode with its transmitter off on 25
  // March 2011 at 00:30 UT"). Deep Impact: NSSDCA 2005-001A (launched 12 January 2005; the
  // impactor struck on 4 July; Hartley 2 on 4 November 2010; "end of mission was announced on 19
  // September" 2013) and Horizons' header for -140 ("Contact was lost Aug 11-14, 2013";
  // "w/data to 2012-Jan-24").
  {
    id: 'deep-rosetta',
    name: 'Rosetta',
    launched: '2004-03-02',
    ended: '2016-09-30',
    klass: 'probe',
    horizonsId: -226,
    note: 'A European Space Agency mission. It went into orbit round comet 67P/Churyumov-Gerasimenko ' +
      'in 2014 and released the lander Philae onto it.',
    end: 'It ended its mission by landing on the comet on 30 September 2016',
  },
  {
    id: 'deep-near',
    name: 'NEAR Shoemaker',
    launched: '1996-02-17',
    ended: '2001-02-12',
    klass: 'probe',
    horizonsId: -93,
    aliases: ['NEAR', 'Near Earth Asteroid Rendezvous'],
    note: 'The first spacecraft to orbit an asteroid and the first to touch down on one: 433 Eros, ' +
      'which it circled for a year from February 2000.',
    end: 'It touched down on Eros on 12 February 2001 and was last heard from on 28 February',
  },
  // `pathEnds`: JPL's file for the Stardust bus stops on 12 March 2011 (Horizons: "No ephemeris
  // ... after A.D. 2011-MAR-12 23:58:53"), twelve days before the end, so the path does too.
  {
    id: 'deep-stardust',
    name: 'Stardust',
    launched: '1999-02-07',
    ended: '2011-03-25',
    pathEnds: '2011-03-12',
    pathEndsWhy: 'The path JPL holds for it stops on 12 March 2011, and stretches of it are ' +
      'JPL’s predictions, not tracking.',
    klass: 'probe',
    horizonsId: -29,
    aliases: ['Stardust-NExT'],
    note: 'It flew through the coma of comet Wild 2 in January 2004, sent a capsule of comet dust ' +
      'back to Earth in 2006, then flew past comet Tempel 1 in 2011.',
    end: 'Its transmitter was switched off on 25 March 2011',
  },
  {
    id: 'deep-deep-impact',
    name: 'Deep Impact',
    launched: '2005-01-12',
    ended: '2013-09-19',
    klass: 'probe',
    horizonsId: -140,
    aliases: ['EPOXI'],
    note: 'It released an impactor that struck comet Tempel 1 on 4 July 2005, then flew past comet ' +
      'Hartley 2 in November 2010.',
    end: 'Contact was lost in August 2013 and NASA declared the mission over on 19 September 2013 ' +
      '(its path after January 2012 is JPL’s prediction, not tracking)',
  },
  // Two more ended missions with their own paths (release 1.7.2). Mariner 2: NASA NSSDCA 1962-041A
  // (launch date 1962-08-27; closest approach to Venus 34 773 km at 19:59:28 UT on 14 December 1962;
  // "The last transmission from Mariner 2 was received on 3 January 1963 at 07:00 UT"), read
  // 2026-11-03; JPL Horizons' -2 header (a trajectory "to 1963-Jan-01 00:00 (CT)", refused after
  // 1962-DEC-31 23:59:25 UT). The Galileo probe: NASA Science's Galileo page (released 12 July 1995 local time,
  // entry 7 December 1995 at 47.6 km per second) and Horizons' -344 header ("RELEASED : Jul 13, 1995", UTC, which `launched` takes; "Probe S960730A
  // (13-Jul-1995 to 07-Dec-1995)", refused after 1995-DEC-07 22:05:58 UT), read 2026-11-03.
  {
    id: 'deep-mariner-2',
    name: 'Mariner 2',
    launched: '1962-08-27',
    ended: '1963-01-03',
    pathEnds: '1962-12-31',
    pathEndsWhy: 'The path JPL holds for it stops on 31 December 1962, before its last signal.',
    klass: 'probe',
    horizonsId: -2,
    note: 'The first spacecraft to fly past another planet: Venus, in December 1962.',
    end: 'Its last signal reached Earth on 3 January 1963',
  },
  {
    id: 'deep-galileo-probe',
    name: 'Galileo Probe',
    launched: '1995-07-13',
    ended: '1995-12-07',
    klass: 'probe',
    horizonsId: -344,
    note: 'Released by Galileo in July 1995, it dived into Jupiter’s atmosphere that December.',
    end: 'It dived into Jupiter’s atmosphere on 7 December 1995',
  },
];

/** Orbits given as elements where I am confident of the shape but not of the phase. */
const ELLIPTIC_CRAFT = [
  {
    id: 'deep-parker',
    name: 'Parker Solar Probe',
    launched: '2018-08-12',
    klass: 'probe',
    horizonsId: -96,
    aAu: 0.388,
    e: 0.88,
    iDeg: 3.4,
    nodeDeg: 244,
    argpDeg: 268,
    note: 'It flies through the Sun’s outer atmosphere at 690 000 km/h behind a carbon ' +
      'heat shield, closer to the Sun than anything has ever been.',
    phaseKnown: false,
  },
  {
    id: 'deep-solar-orbiter',
    name: 'Solar Orbiter',
    launched: '2020-02-10',
    klass: 'probe',
    horizonsId: null,
    aAu: 0.81,
    e: 0.29,
    iDeg: 17,
    nodeDeg: 4,
    argpDeg: 300,
    note: 'It uses Venus to lever itself out of the plane of the planets, so it can photograph ' +
      'the Sun’s poles — which nothing had seen until 2025.',
    phaseKnown: false,
  },
];

/**
 * Construction C. Every element set is MEASURED: JPL Horizons, EPHEM_TYPE=ELEMENTS,
 * CENTER='500@10' (heliocentric, ecliptic J2000 -- the frame of the snapshot vectors), at 00:00 TDB
 * on the row's date, fetched 2026-09-22, rounded to the digits shown. TDB is taken as UTC, as
 * data/parsers.js does for the vectors: that is 69 s late, about 2 000 km along the track at these
 * speeds, and it is the SAME 69 s the snapshot carries, so the stand-in and the real vectors agree
 * with each other. Rounded, each row sits within 400 km of Horizons' position at its date
 * (measured), and tests/test_deep_space.mjs checks that and every `drift` sentence against
 * Horizons' own positions.
 *
 * `drift` is visitor-facing: it ends the card's honesty line, after the sentence that says the
 * orbit is two-body from the stated day. `destination` is printed by the probe card ("on its way
 * to ..."), so it is set only where it stays true for years: BepiColombo and Hera arrive within
 * months of this list being written, and a bundled row does not know when it has.
 */
const OSC_EPOCH_MS = Date.UTC(2026, 8, 22);
const OSC_EPOCH_WORDS = '22 September 2026';

const OSCULATING_CRAFT = [
  {
    id: 'deep-psyche',
    name: 'Psyche',
    launched: '2023-10-13',
    klass: 'probe',
    horizonsId: -255,
    // Horizons -255 header (revised 2026-09-01): Mars flyby 2026-05-15, solar-electric Hall
    // thrusters, captured by 16 Psyche in late July 2029.
    note: 'Past its Mars flyby of May 2026, it is pushing out on electric thrusters to 16 Psyche, ' +
      'a metal-rich asteroid it reaches in 2029.',
    destination: 'the metal-rich asteroid 16 Psyche',
    aAu: 2.078466, e: 0.33187, iDeg: 2.6236, nodeDeg: 160.077, argpDeg: 221.9003, maDeg: 35.8142,
    // Thrusting all the time, so the drift grows with the square of the time: 182 000 km at 30
    // days, 2.17 million at 90.
    drift: 'Checked against JPL’s own forecast, it is 200 000 km out after a month and 2 million ' +
      'km after three.',
  },
  {
    id: 'deep-lucy',
    name: 'Lucy',
    launched: '2021-10-16',
    klass: 'probe',
    horizonsId: -49,
    // Horizons -49 header (revised 2025-10-25): 3548 Eurybates on 2027-08-11 is the first of the
    // five Jupiter Trojans in its flyby list. 5.05 au from the Sun on 2026-09-22 (Horizons).
    note: 'Now 5 au from the Sun, it flies past 3548 Eurybates in August 2027, the first of the ' +
      'five Trojan asteroids sharing Jupiter’s orbit that it will visit.',
    destination: 'Jupiter’s Trojan asteroids',
    aAu: 3.354199, e: 0.71343, iDeg: 4.4203, nodeDeg: 261.2311, argpDeg: 160.4172, maDeg: 106.295,
    // Coasting: 4 000 km at 90 days, 16 000 at 180. Its next burn, DSM-3, is 312.7 m/s on
    // 2027-04-03 (the header's maneuver plan); a year out the drawing is 4.6 million km off.
    drift: 'Checked against JPL’s own forecast, it stays within 16 000 km for six months, until ' +
      'the engine burn planned for April 2027.',
  },
  {
    id: 'deep-europa-clipper',
    name: 'Europa Clipper',
    launched: '2024-10-14',
    klass: 'probe',
    horizonsId: -159,
    // Horizons -159 header (revised 2026-08-18): Earth gravity assist 2026-12-03, Jupiter arrival
    // 2030-04-11, 49 Europa flybys.
    note: 'Heading for Jupiter, it swings past Earth on 3 December 2026 for the speed to arrive in ' +
      'April 2030 and fly past Europa 49 times.',
    destination: 'Jupiter’s moon Europa',
    aAu: 1.599125, e: 0.475425, iDeg: 2.0465, nodeDeg: 71.4327, argpDeg: 302.3992, maDeg: 344.6094,
    // Coasting to the flyby: 12 000 km off on 2026-12-03. The flyby itself is 10 290 km from
    // Earth's centre at 20:20 TDB (Horizons, CENTER='500@399'), and after it this orbit is the one
    // the craft left: 8 million km off by 2026-12-21, 42 million by March. A post-flyby element
    // set was measured too and is worse NOW, which is when this stand-in is used.
    drift: 'Checked against JPL’s own forecast, it stays within 12 000 km until its Earth flyby ' +
      'on 3 December 2026, and is wrong after it.',
  },
  {
    id: 'deep-juice',
    name: 'JUICE',
    launched: '2023-04-14',
    klass: 'probe',
    horizonsId: -28,
    aliases: ['Jupiter Icy Moons Explorer'],
    // Closest approach 2026-09-28 11:50 TDB, 15 248 km from Earth's centre (Horizons, CENTER=
    // '500@399', 10-minute steps). Horizons -28 header (revised 2026-09-21): Earth flyby #2 of the
    // gravity assists that bring it to Jupiter in July 2031.
    note: 'Its Earth flyby of 28 September 2026, 15 000 km from Earth’s centre, is one of the ' +
      'gravity assists that take it to Jupiter in July 2031.',
    destination: 'Jupiter’s moon Ganymede',
    // The elements for 1 OCTOBER, after the flyby, not 22 September: the flyby adds about 4 km/s,
    // so the pre-flyby set was 8 million km off a month later. This one is 2.2 million km off on
    // 22 September, 0.17 million on the 28th, and 111 000 km four months after the flyby.
    epochMs: Date.UTC(2026, 9, 1),
    epochWords: '1 October 2026',
    aAu: 1.632993, e: 0.479749, iDeg: 0.0041, nodeDeg: 176.1322, argpDeg: 246.7938, maDeg: 341.1351,
    drift: 'That is just after its Earth flyby, so checked against JPL’s own forecast it is up to ' +
      '2 million km out before 28 September, and about 110 000 km four months after.',
  },
  {
    id: 'deep-bepicolombo',
    name: 'BepiColombo',
    launched: '2018-10-20',
    klass: 'probe',
    horizonsId: -121,
    // Horizons -121 header (revised 2026-09-21): six Mercury flybys 2021-2025; the 2024-09-02 note
    // on reduced electric propulsion and the one-year delay; Mercury orbit insertion 2026-Nov.
    note: 'Six Mercury flybys behind it, it enters orbit around Mercury in November 2026, a year ' +
      'late after a power fault weakened its thrusters.',
    aAu: 0.393399, e: 0.196236, iDeg: 6.9817, nodeDeg: 48.3583, argpDeg: 26.7043, maDeg: 162.3605,
    // 542 000 km at 30 days, 4.98 million at 60.
    drift: 'Checked against JPL’s own forecast, it is half a million km out after a month and ' +
      '5 million after two.',
  },
  {
    id: 'deep-hera',
    name: 'Hera',
    launched: '2024-10-07',
    klass: 'probe',
    horizonsId: -91,
    // Horizons -91 header (revised 2026-09-01): its objective is the crater DART left on Dimorphos
    // on 2022-09-26; rendezvous manoeuvre 2026-Oct, arrival "Late 2026".
    note: 'ESA’s follow-up to NASA’s DART impact, it reaches the asteroid pair Didymos and ' +
      'Dimorphos in late 2026 to measure the crater DART left.',
    aAu: 1.693677, e: 0.396112, iDeg: 3.3826, nodeDeg: 72.0392, argpDeg: 317.9109, maDeg: 313.4267,
    // 137 000 km at 30 days, 429 000 at 42 as it manoeuvres in; Horizons' file ends 2026-11-04.
    drift: 'Checked against JPL’s own forecast, it is 140 000 km out after a month; that forecast ' +
      'stops on 4 November 2026.',
  },
  {
    id: 'deep-osiris-apex',
    name: 'OSIRIS-APEX',
    launched: '2016-09-08',
    klass: 'probe',
    horizonsId: -64,
    // The same spacecraft under its first mission's name, which is the one people remember.
    aliases: ['OSIRIS-REx'],
    // Horizons -64 header (revised 2025-09-18): sample capsule recovered 2023-09-24, retargeted to
    // Apophis, 2029 rendezvous. Earth flyby 2027-03-16 03:30 TDB, 10 564 km from Earth's centre
    // (Horizons, CENTER='500@399').
    note: 'Having delivered Bennu’s sample in 2023, it is bound for Apophis, which it reaches in ' +
      '2029, with an Earth flyby on 16 March 2027 on the way.',
    destination: 'the asteroid Apophis',
    aAu: 1.06431, e: 0.22684, iDeg: 0.0043, nodeDeg: 353.4003, argpDeg: 95.4774, maDeg: 263.441,
    // Coasting: about 1 000 km at 120 days; 21 000 km on 2027-03-11 as Earth pulls; wrong after.
    drift: 'Checked against JPL’s own forecast, it stays within about 1 000 km for four months, ' +
      'and is wrong after its Earth flyby on 16 March 2027.',
  },
  {
    id: 'deep-hayabusa2',
    name: 'Hayabusa2',
    launched: '2014-12-03',
    klass: 'probe',
    horizonsId: -37,
    // Horizons -37 header (revised 2026-09-01): Ryugu sample returned 2020-12-05, 2001 CC21 flyby
    // 2026-07-05, 1998 KY26 rendezvous July 2031.
    note: 'Its Ryugu sample delivered in 2020, it flew past the asteroid 2001 CC21 in July 2026 ' +
      'and is bound for 1998 KY26, which it reaches in 2031.',
    destination: 'the asteroid 1998 KY26',
    aAu: 0.917504, e: 0.158424, iDeg: 4.3119, nodeDeg: 76.5053, argpDeg: 119.4213, maDeg: 124.0497,
    // 34 000 km at 30 days, 133 000 at 60; Horizons' file ends 2026-11-27.
    drift: 'Checked against JPL’s own forecast, it is 30 000 km out after a month and 130 000 km ' +
      'after two.',
  },
  {
    id: 'deep-stereo-a',
    name: 'STEREO-A',
    launched: '2006-10-26',
    klass: 'probe',
    horizonsId: -234,
    // Horizons -234 header (revised 2026-09-01): launched 2006 to watch the Sun and its coronal
    // mass ejections from ahead of Earth. a = 0.961 au, a 344-day year, so it gains on Earth; it
    // last passed us on 2023-08-17 at 8.3 million km (Horizons, CENTER='500@399'), and on
    // 2026-09-22 it was 70 degrees ahead.
    note: 'Its year is three weeks shorter than ours, so it slowly laps Earth, watching the Sun’s ' +
      'eruptions from the side; it last passed us in August 2023.',
    aAu: 0.96074, e: 0.006769, iDeg: 0.128, nodeDeg: 212.2938, argpDeg: 77.7722, maDeg: 137.8142,
    // 2 000 km at 60 days, 4 000 at 85; Horizons' file ends 2026-12-18.
    drift: 'Checked against JPL’s own forecast, it stays within a few thousand km for two months.',
  },
  {
    id: 'deep-gaia',
    name: 'Gaia',
    launched: '2013-12-19',
    klass: 'telescope',
    horizonsId: -139479,
    // Horizons -139479 header (revised 2025-05-16): science observations ended 2025-01-25, and on
    // 2025-03-28 it "left its orbit around Earth-Sun L2 and was placed in a heliocentric disposal
    // orbit", coming back past Earth every 14 years at about 10 million km. 83.7 million km from
    // Earth on 2026-09-22. It was drawn at L2 (construction A) until that day.
    note: 'It measured the positions and motions of about two billion stars, then in March 2025 ' +
      'was pushed away from L2 onto an orbit of its own round the Sun.',
    aAu: 1.049606, e: 0.025513, iDeg: 0.0786, nodeDeg: 100.5376, argpDeg: 87.9103, maDeg: 137.1475,
    // Nothing steers it now: 277 km at 30 days, 1 663 at 60, 20 184 at 180, 75 806 at a year.
    drift: 'Checked against JPL’s own forecast, it stays within 2 000 km for two months and ' +
      '20 000 km for six.',
  },
  // Two telescopes that are switched off and still there (2026-10-07; internal #389, public #438).
  // Same query as the rows above, fetched 2026-10-07.
  {
    id: 'deep-spitzer',
    name: 'Spitzer Space Telescope',
    launched: '2003-08-25',
    klass: 'telescope',
    horizonsId: -79,
    // Horizons -79 header (revised 2021-09-13): launched Aug 25, 2003; "On 2020-Jan-30, the
    // mission was completed, the spacecraft decommissioned"; it "will drift in its Earth trailing
    // orbit until eventually returning to the vicinity of Earth in ~2051". The trajectory after
    // 2020-01-30 is "a nominal long-term prediction". 2.00 au from Earth on 2026-09-22.
    note: 'An infrared telescope that worked from 2003 to 2020. Switched off, it still circles ' +
      'the Sun on Earth’s track, now on the far side of the Sun from us.',
    aAu: 1.014174, e: 0.01128, iDeg: 1.1331, nodeDeg: 316.2451, argpDeg: 195.7813, maDeg: 35.5983,
    // Nothing steers it: 531 km at 90 days, 6 928 at 180, 32 513 at a year.
    drift: 'Checked against JPL’s own forecast, which is itself a prediction since 2020, it stays ' +
      'within 1 000 km for three months and 7 000 km for six.',
  },
  {
    id: 'deep-kepler',
    name: 'Kepler',
    launched: '2009-03-07',
    klass: 'telescope',
    horizonsId: -227,
    aliases: ['Kepler space telescope', 'K2'],
    // Horizons -227 header (revised 2019-02-22): launched 2009-Mar-7 03:49 UTC to an
    // "Earth-trailing heliocentric orbit"; "Mission completed 2018-Oct-30 due to depletion of
    // fuel"; "Kepler reported detections of 2662 planets". 1.76 au from Earth on 2026-09-22.
    note: 'It found more than 2 600 planets round other stars before its fuel ran out in 2018. ' +
      'It still follows Earth round the Sun, falling further behind.',
    aAu: 1.013113, e: 0.03609, iDeg: 0.4483, nodeDeg: 154.1122, argpDeg: 294.1403, maDeg: 150.3521,
    // 2 494 km at 90 days, 15 456 at 180.
    drift: 'Checked against JPL’s own forecast, which is a prediction since 2018, it stays ' +
      'within 3 000 km for three months and 16 000 km for six.',
  },
];

/** @returns {Array<Object>} */
export function sampleDeepSpace() {
  const out = [];

  for (const c of ANCHORED_CRAFT) {
    const anchor = ANCHORS[c.anchor];
    const aKm = anchor.aAu * AU_KM;
    out.push({
      id: c.id,
      name: c.name,
      layer: 'deep-space',
      klass: c.klass,
      propagator: 'kepler',
      frame: 'sun-inertial',
      cls: 'sample',
      epoch: J2000_MS,
      source: 'horizons-deep-space',
      elements: {
        qKm: aKm * (1 - anchor.e),
        e: anchor.e,
        aKm,
        iRad: anchor.iDeg * DEG,
        omRad: anchor.nodeDeg * DEG,
        wRad: anchor.argpDeg * DEG,
        tpMs: tpFromMeanAnomaly(aKm, anchor.m0Deg * DEG, J2000_MS),
        epochMs: J2000_MS,
        muKm3S2: MU_SUN,
      },
      meta: {
        horizonsId: c.horizonsId,
        construction: 'anchored',
        anchor: anchor.label,
        note: c.note,
        // The day it left the ground, UTC (see LAUNCH_DATES_READ): the card counts its days in space.
        ...(c.launched ? { launchDate: c.launched } : {}),
        periodDays: periodDays(aKm),
        approx: true,
        anchorDrift: anchor.driftNote,
        // Only for craft held near EARTH. Drawn on Earth's own orbit, their distance from Earth is
        // the construction's error -- up to ~1.1 million km, the same order as the real 1.5 million
        // -- so the card printed JWST at "0.4x the Moon's distance". ui/cards.js reads this instead.
        ...(Number.isFinite(c.earthRangeKm) ? { earthRangeKm: c.earthRangeKm } : {}),
        approxFields: ['position offset from ' + anchor.label],
        why: NO_CORS + ' ' + c.offset + ' The orbit itself is the published J2000 element set ' +
          'for ' + anchor.label + ', mean anomaly included, so the place along it is real to ' +
          'within ' + anchor.driftNote + '.',
      },
    });
  }

  for (const c of PLANET_ORBITERS) {
    const world = WORLD_WORDS[c.world];
    const worlds = world === 'the Moon' ? 'the Moon’s' : world + '’s';
    out.push({
      id: c.id,
      name: c.name,
      layer: 'deep-space',
      klass: c.klass,
      propagator: 'orbiter',
      frame: c.world + '-inertial',
      cls: 'sample',
      epoch: ORBITER_EPOCH_MS,
      source: 'horizons-deep-space',
      muKm3S2: WORLD_GM[c.world],
      samples: [{ tMs: ORBITER_EPOCH_MS, rKm: c.rKm, vKmS: c.vKmS }],
      // A bound orbit repeats, so one state answers for any date and the craft never leaves the
      // map; `drift` is the sentence that says how soon the place along the orbit stops being known.
      extrapolateMs: Infinity,
      meta: {
        horizonsId: c.horizonsId,
        construction: 'round-a-world',
        orbits: c.world,
        periodMin: c.periodMin,
        note: c.note,
        // The day it left the ground, UTC (see LAUNCH_DATES_READ): the card counts its days in space.
        ...(c.launched ? { launchDate: c.launched } : {}),
        ...(c.aliases ? { aliases: c.aliases } : {}),
        // What the card says once the harvested snapshot draws it instead (data/parsers.js copies
        // it to `orbitKnown`, which ui/cards.js prints after the class line). Written here, beside
        // the measurement of `arc`, because that measurement is what makes it true: it holds for
        // six-hourly states, and the parser refuses a coarser snapshot rather than overclaim.
        snapshotKnown: 'it ' + c.orbit + '; JPL Horizons gives its place relative to ' + world +
          ' every six hours, and between those it is carried round ' + world + ' by ' + worlds +
          ' pull alone, which keeps it ' + c.arc,
        approx: true,
        approxFields: ['position along the orbit after ' + ORBITER_EPOCH_WORDS],
        // Not NO_CORS alone: for these the server's copy existing is not enough. Every snapshot
        // before 2026-09-22 holds them relative to the Sun, which data/parsers.js refuses.
        why: 'This browser cannot read JPL’s servers itself (they send no CORS header), and a ' +
          'craft this close to ' + world + ' can only be drawn where it is from JPL’s positions ' +
          'relative to ' + world + ', which our own server’s copy does not have at the moment. ' +
          'Until it does, this is data shipped with the app. It ' + c.orbit + '. The dot starts ' +
          'from JPL Horizons’ position for it relative to ' + world + ' on ' + ORBITER_EPOCH_WORDS +
          ' and is carried round ' + world + ' by ' + worlds + ' pull alone. ' + c.drift,
      },
    });
  }

  for (const c of CRUISING_CRAFT) {
    const dirKm = directionUnit(c.lonDeg * DEG, c.latDeg * DEG);
    const rKm = dirKm.map((u) => round(u * c.distanceAu * AU_KM, 4));
    const vKmS = dirKm.map((u) => round(u * c.speedKmS, 4));
    // Straight line through the measured point. At these distances the Sun's pull changes the
    // direction by well under a degree a decade, so a line is the honest shape.
    //
    // The knots sit TWENTY YEARS either side of it, not 180 days. propagate/sampled.js stops
    // answering more than 7 days outside a record's sample range, so a 180-day span meant these
    // three craft would silently stop being drawn in March 2027 — measured, they returned null.
    // Cubic Hermite between two knots with equal, collinear velocities is exactly the straight
    // line, so widening the span changes no drawn position: it only stops the cliff.
    const t0 = Date.UTC(2026, 8, 6);
    const span = 20 * 365.25 * DAY_MS;
    const t1 = t0 + span;
    const tBack = t0 - span;
    const dt = (t1 - t0) / 1000;
    const dtBack = (tBack - t0) / 1000;
    out.push({
      id: c.id,
      name: c.name,
      layer: 'deep-space',
      klass: c.klass,
      propagator: 'sampled',
      frame: 'sun-inertial',
      cls: 'sample',
      epoch: t0,
      source: 'horizons-deep-space',
      samples: [
        // The outer knots are NOT rounded: they are derived from the two rounded figures above,
        // and rounding them again would bend the line by the rounding step.
        { tMs: tBack, rKm: rKm.map((v, i) => v + vKmS[i] * dtBack), vKmS },
        { tMs: t0, rKm, vKmS },
        { tMs: t1, rKm: rKm.map((v, i) => v + vKmS[i] * dt), vKmS },
      ],
      meta: {
        horizonsId: c.horizonsId,
        construction: 'straight-line',
        distanceAu: c.distanceAu,
        speedKmS: c.speedKmS,
        escapeLonDeg: c.lonDeg,
        escapeLatDeg: c.latDeg,
        note: c.note,
        // The day it left the ground, UTC (see LAUNCH_DATES_READ): the card counts its days in space.
        ...(c.launched ? { launchDate: c.launched } : {}),
        approx: true,
        approxFields: c.distanceMeasured
          ? ['escapeLonDeg', 'escapeLatDeg', 'speedKmS']
          : ['distanceAu', 'escapeLonDeg', 'escapeLatDeg', 'speedKmS'],
        why:
          NO_CORS +
          ' This one is built from three numbers: ' +
          c.distanceAu +
          ' au from the Sun' +
          (c.distanceMeasured
            ? ' (checked against JPL Horizons on ' + (c.checkedWords || '7 September 2026') + ')'
            : ' (the published figure, rounded)') +
          ', ' +
          c.speedKmS +
          ' km/s, and an escape direction good to a few degrees. It then moves in a straight ' +
          'line, which at this distance is what it really does.',
      },
    });
  }

  for (const c of LOST_ORBITERS) {
    out.push({
      id: c.id,
      name: c.name,
      layer: 'deep-space',
      klass: c.klass,
      propagator: 'orbiter',
      frame: c.world + '-inertial',
      cls: 'sample',
      epoch: LOST_ORBITER_EPOCH_MS,
      source: 'horizons-deep-space',
      muKm3S2: WORLD_GM[c.world],
      samples: [{ tMs: LOST_ORBITER_EPOCH_MS, rKm: c.rKm, vKmS: c.vKmS }],
      // A bound orbit repeats, so the craft stays on the map; `why` says the place is not known.
      extrapolateMs: Infinity,
      meta: {
        horizonsId: c.horizonsId,
        construction: 'last-tracked-orbit',
        orbits: c.world,
        periodMin: c.periodMin,
        note: c.note,
        launchDate: c.launched,
        lastContact: '2025-12-06',
        ...(c.aliases ? { aliases: c.aliases } : {}),
        approx: true,
        approxFields: ['the whole position: no contact since 6 December 2025'],
        why: c.why,
      },
    });
  }

  for (const c of PAST_CRAFT) {
    out.push({
      id: c.id,
      name: c.name,
      layer: 'deep-space',
      klass: c.klass,
      // No samples: this row answers null at every instant, and its path file answers inside its
      // years once it is here (propagate/index.js EPHEMERIS_OF).
      propagator: 'sampled',
      frame: 'sun-inertial',
      cls: 'inferred',
      epoch: Date.parse(c.ended + 'T00:00:00Z'),
      source: 'horizons-deep-space',
      samples: [],
      meta: {
        horizonsId: c.horizonsId,
        construction: 'own-path',
        note: c.note,
        launchDate: c.launched,
        endDate: c.ended,
        ...(c.pathEnds ? { pathEndDate: c.pathEnds } : {}),
        ...(c.aliases ? { aliases: c.aliases } : {}),
        why: c.end + ', so there is nowhere to draw it today. Between ' + c.launched.slice(0, 4) +
          ' and ' + c.ended.slice(0, 4) + ' it is drawn from its own path: positions from JPL ' +
          'Horizons, shipped with the app and fetched when this card opens. Choose an event ' +
          'of its mission to go there.' + (c.pathEndsWhy ? ' ' + c.pathEndsWhy : ''),
      },
    });
  }

  for (const c of ELLIPTIC_CRAFT) {
    const aKm = c.aAu * AU_KM;
    const epoch = Date.UTC(2026, 0, 1);
    out.push({
      id: c.id,
      name: c.name,
      layer: 'deep-space',
      klass: c.klass,
      propagator: 'kepler',
      frame: 'sun-inertial',
      cls: 'sample',
      epoch,
      source: 'horizons-deep-space',
      elements: {
        qKm: aKm * (1 - c.e),
        e: c.e,
        aKm,
        iRad: c.iDeg * DEG,
        omRad: c.nodeDeg * DEG,
        wRad: c.argpDeg * DEG,
        tpMs: epoch,
        epochMs: epoch,
        muKm3S2: MU_SUN,
      },
      meta: {
        horizonsId: c.horizonsId,
        construction: 'elements-without-phase',
        aAu: c.aAu,
        eccentricity: c.e,
        inclinationDeg: c.iDeg,
        periodDays: periodDays(aKm),
        note: c.note,
        // The day it left the ground, UTC (see LAUNCH_DATES_READ): the card counts its days in space.
        ...(c.launched ? { launchDate: c.launched } : {}),
        approx: true,
        approxFields: ['tpMs', 'aAu', 'eccentricity', 'inclinationDeg', 'nodeDeg', 'argpDeg'],
        why:
          NO_CORS +
          ' The orbit is the published one rounded, so its size, shape and tilt are right. ' +
          'The place along it is not known to this page: it is drawn at its closest point to ' +
          'the Sun on 1 January 2026. Its year is only ' +
          Math.round(periodDays(aKm)) +
          ' days, so treat the dot as a placeholder and the ellipse as the fact.',
      },
    });
  }

  for (const c of OSCULATING_CRAFT) {
    const aKm = c.aAu * AU_KM;
    const epoch = Number.isFinite(c.epochMs) ? c.epochMs : OSC_EPOCH_MS;
    const epochWords = c.epochWords || OSC_EPOCH_WORDS;
    out.push({
      id: c.id,
      name: c.name,
      layer: 'deep-space',
      klass: c.klass,
      propagator: 'kepler',
      frame: 'sun-inertial',
      cls: 'sample',
      epoch,
      source: 'horizons-deep-space',
      elements: {
        qKm: aKm * (1 - c.e),
        e: c.e,
        aKm,
        iRad: c.iDeg * DEG,
        omRad: c.nodeDeg * DEG,
        wRad: c.argpDeg * DEG,
        // The mean anomaly, not a time of perihelion: the phase is KNOWN here, which is the whole
        // difference from ELLIPTIC_CRAFT above.
        maRad: c.maDeg * DEG,
        epochMs: epoch,
        muKm3S2: MU_SUN,
      },
      meta: {
        horizonsId: c.horizonsId,
        construction: 'osculating',
        elementsEpochMs: epoch,
        aAu: c.aAu,
        eccentricity: c.e,
        inclinationDeg: c.iDeg,
        periodDays: periodDays(aKm),
        note: c.note,
        // The day it left the ground, UTC (see LAUNCH_DATES_READ): the card counts its days in space.
        ...(c.launched ? { launchDate: c.launched } : {}),
        ...(c.destination ? { destination: c.destination } : {}),
        // ui/search.js matches meta.aliases at a word start, after the name.
        ...(c.aliases ? { aliases: c.aliases } : {}),
        approx: true,
        approxFields: ['position after ' + epochWords],
        why:
          NO_CORS +
          ' It is drawn on the orbit JPL Horizons gave for it on ' +
          epochWords +
          ' and moved on from there by the Sun’s pull alone, so no engine burn or flyby after ' +
          'that date is in it. ' +
          c.drift,
      },
    });
  }

  return out;
}

// =================================================================================================
// Reentries
// =================================================================================================
//
// Space-Track's `tip` feed is the only public source of reentry windows and it needs a login,
// which a public page cannot hold. So rather than invent a live window, these are three REAL
// reentries that already happened, with the times and places they actually came down. A card
// built on one of these is true; it is just not news.

const REENTRIES = [
  {
    id: 'reentry-tiangong-1',
    name: 'Tiangong-1',
    noradId: 37820,
    reentryIso: '2018-04-02T00:16:00Z',
    windowHours: 2,
    latDeg: -13.6,
    lonDeg: -164.3, // 195.7 E, i.e. 164.3 W — the South Pacific, north-west of Tahiti
    note: "China's first space station. Control was lost in 2016 and it came down over the " +
      'South Pacific two years later; most of it burned, and nothing was reported found.',
  },
  {
    id: 'reentry-cz-5b-y2',
    name: 'Long March 5B core stage (CZ-5B Y2)',
    noradId: 48275,
    reentryIso: '2021-05-08T02:24:00Z',
    windowHours: 2,
    latDeg: 2.65,
    lonDeg: 72.47, // just west of the Maldives
    note: 'A 23-tonne rocket stage left in orbit rather than steered down. It fell into the ' +
      'Indian Ocean near the Maldives, and the argument about uncontrolled reentries has not ' +
      'really stopped since.',
  },
  {
    id: 'reentry-skylab',
    name: 'Skylab',
    noradId: 7816,
    reentryIso: '1979-07-11T16:37:00Z',
    windowHours: 4,
    latDeg: -33.9,
    lonDeg: 121.9, // the debris field around Esperance, Western Australia
    note: "America's first space station. Pieces of it landed in Western Australia, and the " +
      'shire of Esperance fined NASA 400 dollars for littering.',
  },
];

/** @returns {Array<Object>} */
export function sampleReentries() {
  return REENTRIES.map((r) => {
    const tMs = Date.parse(r.reentryIso);
    const half = (r.windowHours / 2) * 3600 * 1000;
    return {
      id: r.id,
      name: r.name,
      layer: 'reentries',
      klass: 'debris',
      propagator: 'fixed',
      frame: 'earth-fixed',
      cls: 'sample',
      epoch: tMs,
      source: 'space-track-tip',
      // The point it is understood to have broken up, at the altitude that usually happens.
      fixed: { latRad: r.latDeg * DEG, lonRad: normLon(r.lonDeg) * DEG, altKm: 80 },
      meta: {
        noradId: r.noradId,
        reentryMs: tMs,
        windowStartMs: tMs - half,
        windowEndMs: tMs + half,
        latDeg: r.latDeg,
        lonDeg: normLon(r.lonDeg),
        altKm: 80,
        note: r.note,
        historic: true,
        approx: true,
        approxFields: ['latDeg', 'lonDeg', 'windowStartMs', 'windowEndMs'],
        why:
          'Space-Track is the only public source of reentry windows and it needs a login, ' +
          'which a page open to everybody cannot hold. So this is not a prediction: it is a ' +
          'reentry that really happened, on the date shown, near the place shown. The time is ' +
          'the published one; the point is where it is understood to have broken up, to within ' +
          'a few hundred kilometres, because a reentry track is long.',
      },
    };
  });
}

// =================================================================================================
// helpers
// =================================================================================================

function periodDays(aKm) {
  if (!Number.isFinite(aKm) || aKm <= 0) return null;
  return (2 * Math.PI * Math.sqrt((aKm * aKm * aKm) / MU_SUN)) / 86400;
}

/** Time of perihelion passage implied by a mean anomaly at an epoch. */
function tpFromMeanAnomaly(aKm, mRad, epochMs) {
  const n = Math.sqrt(MU_SUN / (aKm * aKm * aKm)); // rad/s
  let m = mRad % (2 * Math.PI);
  if (m < 0) m += 2 * Math.PI;
  return Math.round(epochMs - (m / n) * 1000);
}

/** Heliocentric ecliptic J2000 unit vector from an ecliptic longitude and latitude. */
function directionUnit(lonRad, latRad) {
  const c = Math.cos(latRad);
  return [c * Math.cos(lonRad), c * Math.sin(lonRad), Math.sin(latRad)];
}

function round(v, sig) {
  if (!Number.isFinite(v) || v === 0) return v;
  const mag = Math.ceil(Math.log10(Math.abs(v)));
  const f = Math.pow(10, sig - mag);
  return Math.round(v * f) / f;
}

function normLon(deg) {
  let d = deg % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
}

import { SITES } from './sites.js';

/**
 * The ground and surface sites nothing serves as an API: the Deep Space Network's big dishes, the
 * places on the Moon and Mars where something landed. `registry/sites.yaml` is the reviewable copy
 * and data/sites.js its generated mirror; this turns the mirror's rows into records.
 *
 * THIS USED TO BE THE ROWS THEMSELVES, hand-ported, one 22-line literal per site, with nothing
 * checking that a latitude here matched the one in the registry. That was thirteen records until
 * 2026-09-22, when twenty-one landing sites arrived at once: the rows are now generated and
 * `gen_sites_js.py --check` is the check the port never had.
 *
 * These are `measured`, NOT `sample`. A dish does not move and its coordinates are published --
 * hand-kept is a statement about where the data comes from, not about how sure we are of it. The
 * distinction matters because `sample` is drawn with a dashed halo meaning "not a live position",
 * and that would be wrong here.
 *
 * A row that says `record: false` is a coordinate and not a record: Beresheet's impact smudge,
 * which registry/oddities.yaml anchors the lunar library on and which has nothing standing on it
 * to draw.
 *
 * @returns {Array<Object>} one record per sites.yaml row, less the `record: false` ones
 */
export function handKeptSites() {
  return SITES.filter((row) => row.record !== false).map(siteRecord);
}

function siteRecord(row) {
  const meta = { siteKind: row.class, world: row.world, doing: row.doing };
  if (row.diameter_m != null) meta.diameterM = row.diameter_m;
  // What the scene draws on a surface row: scene/realmodels.js routes `lander` and `rover` to a
  // procedural stand-in and the card says so. `siteKind` stays the row's class, because that is
  // what it has always meant to the card and to the pad and dish routes.
  if (row.shape) meta.siteShape = row.shape;
  // A hand-kept pad's vehicle is drawn only around these instants (scene/realmodels.js padVehicleShown).
  if (Array.isArray(row.liftoffs)) meta.liftoffs = row.liftoffs.slice();
  // Other names search matches (ui/search.js reads meta.aliases): Sojourner, Tranquility Base.
  if (Array.isArray(row.aliases) && row.aliases.length) meta.aliases = row.aliases.slice();
  meta.latDeg = row.lat;
  meta.lonDeg = row.lon;
  return {
    id: row.id,
    name: row.display,
    klass: 'site',
    layer: 'hand-kept-sites',
    propagator: 'fixed',
    // The row's own world. A lunar site that said 'earth-fixed' was drawn on Earth for months;
    // see scene/stage.js.
    frame: `${row.world}-fixed`,
    cls: 'measured',
    epoch: null,
    source: 'registry/sites.yaml',
    fixed: { latDeg: row.lat, lonDeg: row.lon, altKm: (row.alt_m || 0) / 1000 },
    meta,
  };
}

// =================================================================================================
// Odd things we sent
// =================================================================================================
//
// registry/oddities.yaml -> records, hand-written here for the same reason data/rocketmatch.js is
// hand-written next to the generated data/rockets.js: generated data and code never share a file,
// so `gen_oddities_js.py --check` is a plain comparison with nothing to preserve.
//
// THESE ARE NOT `sample` RECORDS. `sample` means a bundled stand-in for a live feed a browser
// cannot call, and it draws with a dashed halo saying "not a live position". These rows are not
// standing in for anything -- they ARE the thing, the same distinction handKeptSites() makes about
// the Goldstone dish. Each record carries the row's own `position_class` as its cls.
//
// THREE THINGS THIS EMITTER DOES THAT THE NEXT READER WILL WANT TO "FIX":
//
//  1. THE TWO EPOCHS. `record.epoch` is the row's `evidence_epoch` -- when anybody last LOOKED --
//     and `elements.epochMs` is the osculating epoch the propagator integrates from. For the
//     Roadster they are eight and a half years apart. cards.js prints the age of record.epoch, so
//     setting it to the osculating epoch would print "elements 0 days old", which is true of the
//     arithmetic and a lie about the knowledge. check_registry.py refuses an in_orbit row with no
//     evidence_epoch for exactly this reason.
//
//  2. AN `attached` ROW IS NOT A RECORD. The Golden Record at Voyager 1's exact position would be
//     a second dot under the first: ambiguous to tap (main.js takes the first hit in layer order)
//     and, if it carried a horizons_id, a second Voyager drawn beside the first
//     (scene/realmodels.js matches on that id). It is drawn as a CHILD of its carrier's model
//     instead, and reached from the carrier's card; data/attached.js owns that, including the
//     rule that its position is the carrier's copied verbatim. The layer's count line says how
//     many rows those are rather than quietly omitting them.
//
//  3. AN `unknown` ROW GETS NO PROPAGATOR AT ALL. `propagate()` returns null for a record whose
//     propagator is not in its table, so the glyph layer skips it, heroes.js skips it and the
//     camera has nothing to fly to -- which is the point. It is still a record, so search finds
//     it and the card opens and says, in words, that nobody knows where it is. A dot would have
//     been a guess, and every other layer on this map has taught the visitor that a dot is a claim.

import { ODDITIES } from './oddities.js';
import { timeText } from '../copy/en.js';

/** JD -> ms. 2440587.5 is the Julian Date of the Unix epoch. */
function jdToMs(jd) {
  return (jd - 2440587.5) * DAY_MS;
}

/** The evidence date as ms, or null. `2018-03-19` is a YAML date and arrives as a string. */
function evidenceMs(value) {
  if (!value) return null;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Everything the card needs that is true of every row, whatever kind of place it is in. */
function commonMeta(row) {
  const shape = row.shape || {};
  const kind = row.where ? row.where.kind : null;
  // A ROW NOBODY CAN PLACE IS NOT DRAWN, so it says nothing about a drawing. `unknown` gets no
  // propagator (note 3 above), which means no dot, no model and nothing for the card to describe
  // -- and "drawn as a generic object; we have no shape for a lapel pin" under a card with no
  // position would be the card describing a shape that is not on the screen. The registry refuses
  // any build but `generic` on such a row; this is the other half of the same rule.
  const drawn = kind !== 'unknown';
  return {
    fact: row.fact,
    myths: Array.isArray(row.myths) ? row.myths : [],
    cite: row.cite || null,
    asOf: row.as_of || null,
    whereKind: kind,
    drawnName: shape.drawn_name || null,
    // Which ODDITY_BUILDERS row scene/models.js draws, and what the card is allowed to claim
    // about it. `drawsAs` is the row's own `stands_for`, so the card repeats the registry rather
    // than guessing: `variant` published dimensions, `family` the kind of thing, `generic` we
    // have no shape. `departure` is where the drawing knowingly differs and says so.
    modelVariant: drawn ? shape.build || 'generic' : null,
    drawsAs: drawn ? shape.stands_for || 'generic' : null,
    departure: drawn ? shape.departure || null : null,
    // Which way the shape points, when the row says. Absent is the default and reproduces what
    // scene/models.js does today: a constant seeded from the record id. The surface branches
    // below overwrite this with `up`, and check_registry.py refuses the field on those rows so
    // the two can never disagree.
    attitude: drawn ? shape.attitude || null : null,
  };
}

/**
 * The layer's records. Six of the eight rows; see note 2 above for the two that are not here.
 * @returns {Array<Object>}
 */
export function sampleOddities() {
  const out = [];
  for (const row of ODDITIES) {
    const w = row.where || {};
    const base = {
      id: row.id,
      name: row.display,
      layer: 'oddities',
      klass: row.klass || 'oddity',
      source: 'registry/oddities.yaml',
      epoch: null,
      meta: commonMeta(row),
    };

    if (w.kind === 'in_orbit') {
      const el = w.elements || {};
      const aKm = el.a_au * AU_KM;
      const prov = row.orbit_provenance || {};
      out.push({
        ...base,
        propagator: 'kepler',
        frame: w.frame || 'sun-inertial',
        cls: row.position_class,
        // (1) above: the age the card prints is the age of the EVIDENCE.
        epoch: evidenceMs(w.evidence_epoch),
        elements: {
          qKm: aKm * (1 - el.e),
          e: el.e,
          aKm,
          iRad: el.i_deg * DEG,
          omRad: el.node_deg * DEG,
          wRad: el.argp_deg * DEG,
          // The phase is PUBLISHED for this one, so it is real and not the placeholder honesty
          // rule 3 describes: no `approx` flag and no "drawn at perihelion" apology.
          tpMs: jdToMs(el.tp_jd),
          epochMs: jdToMs(el.epoch_jd),
          muKm3S2: MU_SUN,
        },
        meta: {
          ...base.meta,
          horizonsId: w.horizons_id || null,
          aAu: el.a_au,
          eccentricity: el.e,
          inclinationDeg: el.i_deg,
          periodDays: periodDays(aKm),
          arcEnd: w.evidence_epoch ? String(w.evidence_epoch) : null,
          obsCount: prov.obs_count ?? null,
          arc: prov.arc || null,
          orbitCaveat: prov.caveat || null,
          solution: prov.solution || null,
        },
      });
      continue;
    }

    if (w.kind === 'on_surface') {
      const obj = w.object || {};
      const anchor = w.anchor || null;
      const latDeg = anchor ? anchor.lat : obj.lat;
      const lonDeg = anchor ? anchor.lon : obj.lon;
      out.push({
        ...base,
        propagator: 'fixed',
        frame: `${w.world}-fixed`,
        cls: row.position_class,
        fixed: { latDeg, lonDeg, altKm: 0 },
        meta: {
          ...base.meta,
          world: w.world,
          latDeg,
          lonDeg,
          // A photograph lying in lunar dust stands on a surface, so the model does too.
          // scene/models.js updateModelAttitude() reads this and puts +Y away from the centre;
          // without it an oddity would take the class default, which is the seeded constant a
          // tumbling car wants and a print lying flat does not.
          attitude: 'up',
          // The two numbers, kept apart all the way to the card. One field would have to choose
          // between 0.4 m and 40 m, and either choice is false.
          anchorName: anchor ? anchor.of : null,
          anchorUncertaintyM: anchor ? anchor.uncertainty_m : null,
          objectPrecisionM: obj.precision_m ?? null,
          objectHow: obj.how || null,
        },
      });
      continue;
    }

    if (w.kind === 'came_home') {
      out.push({
        ...base,
        propagator: 'fixed',
        frame: 'earth-fixed',
        cls: row.position_class,
        fixed: { latDeg: w.lat, lonDeg: w.lon, altKm: 0 },
        meta: {
          ...base.meta,
          world: 'earth',
          latDeg: w.lat,
          lonDeg: w.lon,
          attitude: 'up',   // a museum case stands on the ground, like every other fixed record
          whereKept: w.where_kept || null,
          leftSpace: w.left_space ? String(w.left_space) : null,
        },
      });
      continue;
    }

    if (w.kind === 'unknown') {
      out.push({
        ...base,
        // (3) above. No propagator, so propagate() answers null and nothing is drawn.
        propagator: null,
        frame: null,
        cls: row.position_class,
        meta: {
          ...base.meta,
          unplaceable: true,
          lastKnown: w.last_known || null,
          whyUnknown: w.why_unknown || null,
          wouldNeed: w.would_need || null,
        },
      });
      continue;
    }

    // `attached` falls through deliberately -- see note 2. Any other kind is refused by
    // check_registry.py before it reaches here, so there is nothing to guess about.
  }
  return out;
}

// How many rows are riding on a spacecraft the app draws rather than carrying their own dot.
// Re-exported rather than recomputed: data/attached.js owns the attached rows now that they are
// drawn, and two filters over the same registry would be two places to forget a kind.
export { attachedOddityCount } from './attached.js';
